import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import axios from 'axios';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription } from './components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { useToast } from './hooks/use-toast';
import { Toaster } from './components/ui/toaster';
import { 
  GraduationCap, 
  Users, 
  FileText, 
  BarChart3, 
  Mail, 
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Shield,
  BookOpen,
  UserCheck,
  Settings,
  Upload,
  Download,
  Trash2,
  Eye,
  User,
  Lock,
  Home,
  MessageSquare,
  LogOut,
  Send,
  Bot,
  Minus,
  ArrowLeft,
  Menu,
  X,
  ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const N8N_CHAT_URL = process.env.REACT_APP_N8N_CHAT_URL || 'https://n8n.leaderdreams.com/webhook/chatbot-afd';
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Simple routing system
const getCurrentRoute = () => {
  const path = window.location.pathname;
  if (path === '/campus') return 'campus';
  return 'admin';
};

const navigateTo = (route) => {
  if (route === 'campus') {
    window.history.pushState({}, '', '/campus');
  } else {
    window.history.pushState({}, '', '/');
  }
  window.dispatchEvent(new PopStateEvent('popstate'));
};

// Utilidad para formatear fechas
const formatDate = (dateString) => {
  if (!dateString) return 'No especificada';
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Componente de estadísticas del dashboard
const DashboardStats = ({ stats }) => {
  const statCards = [
    {
      title: 'Solicitudes Totales',
      value: stats?.summary?.total_requests || 0,
      icon: FileText,
      color: 'bg-blue-500'
    },
    {
      title: 'Solicitudes Pendientes',
      value: stats?.summary?.pending_requests || 0,
      icon: Clock,
      color: 'bg-orange-500'
    },
    {
      title: 'Cursos Activos',
      value: stats?.summary?.active_courses || 0,
      icon: GraduationCap,
      color: 'bg-green-500'
    },
    {
      title: 'Total Cursos',
      value: stats?.summary?.total_courses || 0,
      icon: BookOpen,
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {statCards.map((stat, index) => {
        const IconComponent = stat.icon;
        return (
          <Card key={index} className="relative overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg text-white`}>
                  <IconComponent size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Componente de Login
const LoginForm = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLogging, setIsLogging] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLogging(true);
    const success = await onLogin(credentials.username, credentials.password);
    setIsLogging(false);
    
    if (success) {
      setCredentials({ username: '', password: '' });
    }
  };

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-600" />
          Acceso al Panel de Gestión
        </CardTitle>
        <CardDescription>
          Ingrese sus credenciales para acceder al sistema de gestión AFD
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="username">Usuario / Email</Label>
            <Input
              id="username"
              type="email"
              required
              value={credentials.username}
              onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
              placeholder="tu.email@ejemplo.com"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              type="password"
              required
              value={credentials.password}
              onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Ingrese su contraseña"
              className="mt-1"
            />
          </div>

          <Button 
            type="submit" 
            disabled={isLogging || !credentials.username || !credentials.password}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {isLogging ? (
              <>
                <Clock className="mr-2 h-4 w-4 animate-spin" />
                Iniciando sesión...
              </>
            ) : (
              <>
                <UserCheck className="mr-2 h-4 w-4" />
                Iniciar Sesión
              </>
            )}
          </Button>

          <div className="text-center pt-2">
            <p className="text-xs text-gray-500">
              <Shield className="inline h-3 w-3 mr-1" />
              Formulario protegido
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Componente del formulario de solicitud de información
const CourseRequestForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    course_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [availableCourses, setAvailableCourses] = useState([]);
  const { toast } = useToast();

  // Cargar cursos disponibles desde la API
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API}/courses`);
        const courses = response.data.courses || response.data || [];
        setAvailableCourses(courses.filter(c => c.is_active !== false));
      } catch (error) {
        console.error('Error cargando cursos:', error);
        // Fallback si no hay cursos en BD
        setAvailableCourses([]);
      }
    };
    fetchCourses();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const executeRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY !== 'your-recaptcha-site-key-here') {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'course_request' })
            .then(token => {
              console.log('✅ reCAPTCHA token real generado');
              resolve(token);
            });
        });
      } else {
        // Para desarrollo sin reCAPTCHA configurado
        console.log('🔧 Usando token de desarrollo (reCAPTCHA no configurado)');
        resolve('development-token');
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Ejecutar reCAPTCHA
      const token = await executeRecaptcha();
      setRecaptchaToken(token);

      // Preparar datos del formulario
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('course_type', formData.course_type);
      submitData.append('message', formData.message || '');
      submitData.append('recaptcha_token', token);

      // Enviar solicitud
      const response = await axios.post(`${API}/course-requests`, submitData);

      if (response.data.success) {
        toast({
          title: "¡Solicitud enviada correctamente!",
          description: `Tu solicitud ha sido recibida. Referencia: ${response.data.reference}`,
        });

        // Limpiar formulario
        setFormData({ name: '', email: '', course_type: '', message: '' });
        
        if (onSuccess) onSuccess();
      }

    } catch (error) {
      console.error('Error enviando solicitud:', error);
      console.error('Response data:', error.response?.data);
      
      let errorMessage = 'No se pudo enviar la solicitud. Por favor, inténtalo más tarde.';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail.message || error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast({
        title: "Error al enviar solicitud",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GraduationCap className="h-6 w-6 text-blue-600" />
          Solicitud de Información - Cursos AFD
        </CardTitle>
        <CardDescription>
          Complete este formulario para recibir información sobre nuestros cursos AFD
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nombre completo *</Label>
            <Input
              id="name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Ingrese su nombre completo"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email">Correo electrónico *</Label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="ejemplo@correo.com"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="course_type">Curso de interés *</Label>
            <Select value={formData.course_type} onValueChange={(value) => handleInputChange('course_type', value)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccione un curso (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">Información general de cursos</SelectItem>
                {availableCourses.map((course) => (
                  <SelectItem key={course.id} value={course.id}>
                    {course.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="message">Mensaje adicional (opcional)</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="Comente cualquier pregunta específica o información adicional..."
              className="mt-1 min-h-[100px]"
            />
          </div>

          <div className="pt-4">
            <Button 
              type="submit" 
              disabled={isSubmitting || !formData.name || !formData.email}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Enviando solicitud...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Enviar solicitud de información
                </>
              )}
            </Button>
          </div>

          <div className="text-center pt-2">
            <p className="text-xs text-gray-500">
              <Shield className="inline h-3 w-3 mr-1" />
              Formulario protegido con reCAPTCHA v3
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Componente de logs de auditoría
const AuditLogsComponent = ({ authToken }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.get(`${API}/audit-logs?limit=50`, { headers });
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Error obteniendo logs de auditoría:', error);
      toast({
        title: "Error",
        description: error.response?.status === 401 
          ? "Debe iniciar sesión para ver los logs de auditoría"
          : "No se pudieron cargar los logs de auditoría",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const actionConfig = {
      CREATE_COURSE_REQUEST: { label: 'Solicitud Creada', color: 'bg-blue-100 text-blue-800' },
      UPDATE_REQUEST_STATUS: { label: 'Estado Actualizado', color: 'bg-green-100 text-green-800' },
      SEND_EMAIL: { label: 'Email Enviado', color: 'bg-purple-100 text-purple-800' },
      VIEW_AUDIT_LOGS: { label: 'Logs Consultados', color: 'bg-gray-100 text-gray-800' },
      LIST_COURSE_REQUESTS: { label: 'Solicitudes Consultadas', color: 'bg-yellow-100 text-yellow-800' }
    };

    const config = actionConfig[action] || { label: action, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Clock className="mr-2 h-4 w-4 animate-spin" />
            Cargando logs de auditoría...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Logs de Auditoría ISO 9001
        </CardTitle>
        <CardDescription>
          Registro completo de todas las actividades del sistema para cumplimiento normativo
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha/Hora</TableHead>
                <TableHead>Acción</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead>Entidad</TableHead>
                <TableHead>Detalles</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-mono text-sm">
                    {formatDate(log.timestamp)}
                  </TableCell>
                  <TableCell>
                    {getActionBadge(log.action)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{log.user_email}</div>
                      <div className="text-xs text-gray-500">{log.user_id}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{log.entity_type}</div>
                      <div className="text-xs text-gray-500">{log.entity_id}</div>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="text-sm">
                      {typeof log.details === 'object' ? (
                        <div className="space-y-1">
                          {Object.entries(log.details).slice(0, 3).map(([key, value]) => (
                            <div key={key} className="text-xs">
                              <span className="font-medium">{key}:</span> {String(value)}
                            </div>
                          ))}
                        </div>
                      ) : (
                        log.details
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {log.ip_address || 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {auditLogs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p>No se encontraron registros de auditoría</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Componente de gestión de plantillas de mensajes
const MessageTemplatesManager = ({ authToken }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    message_type: 'info',
    priority: 'normal'
  });
  const { toast } = useToast();

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/management/messages/templates`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error obteniendo plantillas:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las plantillas",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchTemplates();
    }
  }, [authToken]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      body: '',
      message_type: 'info',
      priority: 'normal'
    });
    setEditingTemplate(null);
    setShowCreateForm(false);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      message_type: template.message_type || 'info',
      priority: template.priority || 'normal'
    });
    setShowCreateForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.subject || !formData.body) {
      toast({
        title: "Error",
        description: "Nombre, asunto y contenido son obligatorios",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingTemplate) {
        await axios.put(
          `${API}/management/messages/templates/${editingTemplate.id}`,
          formData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast({
          title: "¡Plantilla actualizada!",
          description: `La plantilla "${formData.name}" se ha actualizado correctamente.`
        });
      } else {
        await axios.post(
          `${API}/management/messages/templates`,
          formData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast({
          title: "¡Plantilla creada!",
          description: `La plantilla "${formData.name}" se ha creado correctamente.`
        });
      }
      
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Error guardando plantilla:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo guardar la plantilla",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`¿Estás seguro de eliminar la plantilla "${template.name}"?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/management/messages/templates/${template.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast({
        title: "Plantilla eliminada",
        description: `La plantilla "${template.name}" ha sido eliminada.`
      });
      fetchTemplates();
    } catch (error) {
      console.error('Error eliminando plantilla:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la plantilla",
        variant: "destructive"
      });
    }
  };

  const messageTypeLabels = {
    welcome: { label: '👋 Bienvenida', color: 'bg-green-100 text-green-800' },
    reminder: { label: '⏰ Recordatorio', color: 'bg-yellow-100 text-yellow-800' },
    info: { label: 'ℹ️ Información', color: 'bg-blue-100 text-blue-800' },
    approval: { label: '✅ Aprobación', color: 'bg-emerald-100 text-emerald-800' },
    rejection: { label: '❌ Rechazo', color: 'bg-red-100 text-red-800' }
  };

  const priorityLabels = {
    normal: { label: 'Normal', color: 'bg-gray-100 text-gray-800' },
    high: { label: '⚠️ Alta', color: 'bg-orange-100 text-orange-800' },
    urgent: { label: '🚨 Urgente', color: 'bg-red-100 text-red-800' }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando plantillas...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Plantillas de Mensajes
              </CardTitle>
              <CardDescription>
                Gestiona las plantillas predefinidas para comunicaciones con estudiantes
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setShowCreateForm(true); }}>
              + Nueva Plantilla
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Variables disponibles:</strong> Usa <code className="bg-gray-100 px-1 rounded">{'{nombre}'}</code> para insertar el nombre del estudiante automáticamente.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Formulario de creación/edición */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nombre de la plantilla *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Ej: Bienvenida al curso"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="subject">Asunto del email *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Ej: Bienvenida a tu curso AFD"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de mensaje</Label>
                  <Select value={formData.message_type} onValueChange={(value) => handleInputChange('message_type', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="welcome">👋 Bienvenida</SelectItem>
                      <SelectItem value="reminder">⏰ Recordatorio</SelectItem>
                      <SelectItem value="info">ℹ️ Información</SelectItem>
                      <SelectItem value="approval">✅ Aprobación</SelectItem>
                      <SelectItem value="rejection">❌ Rechazo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prioridad</Label>
                  <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">📝 Normal</SelectItem>
                      <SelectItem value="high">⚠️ Alta</SelectItem>
                      <SelectItem value="urgent">🚨 Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="body">Contenido del mensaje *</Label>
                <Textarea
                  id="body"
                  value={formData.body}
                  onChange={(e) => handleInputChange('body', e.target.value)}
                  placeholder="Escribe el contenido del mensaje. Usa {nombre} para insertar el nombre del estudiante."
                  rows={8}
                  required
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingTemplate ? 'Actualizar' : 'Crear'} Plantilla
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Lista de plantillas */}
      <Card>
        <CardHeader>
          <CardTitle>Plantillas Activas ({templates.filter(t => t.is_active !== false).length})</CardTitle>
        </CardHeader>
        <CardContent>
          {templates.filter(t => t.is_active !== false).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay plantillas configuradas</p>
              <p className="text-sm">Haz clic en "Nueva Plantilla" para crear la primera</p>
            </div>
          ) : (
            <div className="space-y-4">
              {templates.filter(t => t.is_active !== false).map((template) => (
                <div 
                  key={template.id} 
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{template.name}</h3>
                        <Badge className={messageTypeLabels[template.message_type]?.color || 'bg-gray-100'}>
                          {messageTypeLabels[template.message_type]?.label || template.message_type}
                        </Badge>
                        <Badge className={priorityLabels[template.priority]?.color || 'bg-gray-100'}>
                          {priorityLabels[template.priority]?.label || template.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Asunto:</strong> {template.subject}
                      </p>
                      <p className="text-sm text-gray-500 line-clamp-2">
                        {template.body}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Creada por: {template.created_by} | {formatDate(template.created_at)}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEdit(template)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDelete(template)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Componente de gestión de categorías de documentos
const DocumentCategoriesManager = ({ authToken }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    key: '',
    name: '',
    description: '',
    required: false,
    accepted_formats: ['.pdf', '.jpg', '.jpeg', '.png'],
    max_size_mb: 5,
    order: 1
  });
  const { toast } = useToast();

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/management/document-categories`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error obteniendo categorías:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las categorías",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchCategories();
    }
  }, [authToken]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      key: '',
      name: '',
      description: '',
      required: false,
      accepted_formats: ['.pdf', '.jpg', '.jpeg', '.png'],
      max_size_mb: 5,
      order: categories.length + 1
    });
    setEditingCategory(null);
    setShowCreateForm(false);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      key: category.key,
      name: category.name,
      description: category.description || '',
      required: category.required || false,
      accepted_formats: category.accepted_formats || ['.pdf', '.jpg', '.jpeg', '.png'],
      max_size_mb: category.max_size_mb || 5,
      order: category.order || 1
    });
    setShowCreateForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.key || !formData.name) {
      toast({
        title: "Error",
        description: "Clave y nombre son obligatorios",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingCategory) {
        await axios.put(
          `${API}/management/document-categories/${editingCategory.id}`,
          formData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast({
          title: "¡Categoría actualizada!",
          description: `La categoría "${formData.name}" se ha actualizado correctamente.`
        });
      } else {
        await axios.post(
          `${API}/management/document-categories`,
          formData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast({
          title: "¡Categoría creada!",
          description: `La categoría "${formData.name}" se ha creado correctamente.`
        });
      }
      
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error guardando categoría:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo guardar la categoría",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`¿Estás seguro de eliminar la categoría "${category.name}"?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/management/document-categories/${category.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast({
        title: "Categoría eliminada",
        description: `La categoría "${category.name}" ha sido eliminada.`
      });
      fetchCategories();
    } catch (error) {
      console.error('Error eliminando categoría:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la categoría",
        variant: "destructive"
      });
    }
  };

  const toggleFormat = (format) => {
    const current = formData.accepted_formats || [];
    if (current.includes(format)) {
      handleInputChange('accepted_formats', current.filter(f => f !== format));
    } else {
      handleInputChange('accepted_formats', [...current, format]);
    }
  };

  const availableFormats = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.gif'];

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando categorías...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Categorías de Documentos
              </CardTitle>
              <CardDescription>
                Configura las categorías de documentos que los estudiantes pueden subir
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setShowCreateForm(true); }}>
              + Nueva Categoría
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Las categorías marcadas como <strong>obligatorias</strong> serán requeridas para completar la matrícula del estudiante.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Formulario de creación/edición */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="key">Clave única *</Label>
                  <Input
                    id="key"
                    value={formData.key}
                    onChange={(e) => handleInputChange('key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    placeholder="Ej: certificado_medico"
                    disabled={!!editingCategory}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Identificador único, sin espacios</p>
                </div>
                <div>
                  <Label htmlFor="name">Nombre visible *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Ej: Certificado Médico"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Instrucciones o detalles sobre este documento"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="required"
                    checked={formData.required}
                    onChange={(e) => handleInputChange('required', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="required" className="text-sm font-medium">
                    Documento obligatorio
                  </Label>
                </div>
                <div>
                  <Label htmlFor="max_size_mb">Tamaño máximo (MB)</Label>
                  <Input
                    id="max_size_mb"
                    type="number"
                    min="1"
                    max="50"
                    value={formData.max_size_mb}
                    onChange={(e) => handleInputChange('max_size_mb', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <Label htmlFor="order">Orden</Label>
                  <Input
                    id="order"
                    type="number"
                    min="1"
                    value={formData.order}
                    onChange={(e) => handleInputChange('order', parseInt(e.target.value))}
                  />
                </div>
              </div>

              <div>
                <Label>Formatos aceptados</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {availableFormats.map((format) => (
                    <button
                      key={format}
                      type="button"
                      onClick={() => toggleFormat(format)}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        formData.accepted_formats?.includes(format)
                          ? 'bg-blue-100 border-blue-300 text-blue-700'
                          : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {format}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingCategory ? 'Actualizar' : 'Crear'} Categoría
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Lista de categorías */}
      <Card>
        <CardHeader>
          <CardTitle>Categorías Configuradas ({categories.filter(c => c.is_active !== false).length})</CardTitle>
        </CardHeader>
        <CardContent>
          {categories.filter(c => c.is_active !== false).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay categorías configuradas</p>
              <p className="text-sm">Haz clic en "Nueva Categoría" para crear la primera</p>
            </div>
          ) : (
            <div className="space-y-3">
              {categories.filter(c => c.is_active !== false).sort((a, b) => a.order - b.order).map((category) => (
                <div 
                  key={category.id} 
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-gray-400">#{category.order}</span>
                        <h3 className="font-semibold">{category.name}</h3>
                        {category.required && (
                          <Badge variant="destructive" className="text-xs">Obligatorio</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        Clave: <code className="bg-gray-100 px-1 rounded">{category.key}</code>
                      </p>
                      {category.description && (
                        <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                      )}
                      <div className="flex flex-wrap gap-1">
                        {category.accepted_formats?.map((format) => (
                          <span key={format} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {format}
                          </span>
                        ))}
                        <span className="text-xs text-gray-500 ml-2">
                          Máx: {category.max_size_mb}MB
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEdit(category)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDelete(category)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Componente principal de Configuración con pestañas
const ConfigurationPanel = ({ authToken }) => {
  const [activeConfigTab, setActiveConfigTab] = useState('templates');

  return (
    <div className="space-y-6">
      {/* Header con logo */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <img 
              src="/logo_icon.png" 
              alt="Nova Dixital" 
              className="h-12 w-12 rounded-full"
            />
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configuración del Sistema
              </CardTitle>
              <CardDescription>
                Personaliza las plantillas de mensajes y categorías de documentos
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Pestañas de configuración */}
      <Tabs value={activeConfigTab} onValueChange={setActiveConfigTab}>
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Plantillas de Mensajes
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Categorías de Documentos
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Sistema
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          <MessageTemplatesManager authToken={authToken} />
        </TabsContent>

        <TabsContent value="categories">
          <DocumentCategoriesManager authToken={authToken} />
        </TabsContent>

        <TabsContent value="system">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Configuración del Sistema
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span>reCAPTCHA v3:</span>
                    <Badge variant={RECAPTCHA_SITE_KEY !== 'your-recaptcha-site-key-here' ? 'default' : 'secondary'}>
                      {RECAPTCHA_SITE_KEY !== 'your-recaptcha-site-key-here' ? 'Configurado' : 'Pendiente'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Entorno:</span>
                    <Badge variant="outline">
                      {process.env.REACT_APP_ENVIRONMENT || 'Producción'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Versión:</span>
                    <Badge variant="outline">
                      {process.env.REACT_APP_VERSION || '2.0.0'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Cumplimiento ISO 9001
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    Todos los registros de auditoría se mantienen por 7 años según normativa ISO 9001.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Información del Sistema
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Backend URL</p>
                    <p className="font-mono text-xs truncate">{process.env.REACT_APP_BACKEND_URL}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Google OAuth</p>
                    <Badge variant={GOOGLE_CLIENT_ID ? 'default' : 'secondary'}>
                      {GOOGLE_CLIENT_ID ? 'Configurado' : 'No configurado'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-gray-500">Base de datos</p>
                    <Badge variant="default">MongoDB Atlas</Badge>
                  </div>
                  <div>
                    <p className="text-gray-500">Almacenamiento</p>
                    <Badge variant="default">Local</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Componente de gestión de administradores/gestores
const AdminUserManagement = ({ authToken, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'gestor' });
  const { toast } = useToast();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      if (error.response?.status === 403) {
        toast({
          title: "Acceso denegado",
          description: "Solo el administrador principal puede gestionar usuarios",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data.success) {
        toast({
          title: "Usuario creado",
          description: `${newUser.name} ha sido añadido como ${newUser.role}`,
        });
        setShowCreateForm(false);
        setNewUser({ name: '', email: '', password: '', role: 'gestor' });
        fetchUsers();
      }
    } catch (error) {
      console.error('Error creando usuario:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo crear el usuario",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}`, 
        { is_active: !user.is_active },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      
      toast({
        title: user.is_active ? "Usuario desactivado" : "Usuario activado",
        description: `${user.name} ha sido ${user.is_active ? 'desactivado' : 'activado'}`,
      });
      fetchUsers();
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el usuario",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Estás seguro de eliminar a ${user.name}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${user.id}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      toast({
        title: "Usuario eliminado",
        description: `${user.name} ha sido eliminado`,
      });
      fetchUsers();
    } catch (error) {
      console.error('Error eliminando usuario:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo eliminar el usuario",
        variant: "destructive",
      });
    }
  };

  // Solo mostrar si es superadmin
  if (currentUser?.email !== 'admin@afd.novadixital.es' && currentUser?.role !== 'superadmin') {
    return (
      <div className="text-center py-8 text-gray-500">
        <Lock className="mx-auto h-12 w-12 mb-4 opacity-50" />
        <p>Solo el administrador principal puede gestionar usuarios</p>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-8"><Clock className="animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Administradores y Gestores</h3>
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          {showCreateForm ? 'Cancelar' : '+ Añadir Usuario'}
        </Button>
      </div>

      {showCreateForm && (
        <Card className="bg-blue-50">
          <CardContent className="pt-4">
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre *</Label>
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Contraseña *</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Rol</Label>
                  <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gestor">Gestor</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit">Crear Usuario</Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Rol</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Último acceso</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Admin principal */}
          <TableRow className="bg-blue-50">
            <TableCell className="font-medium">Administrador Principal</TableCell>
            <TableCell>admin@afd.novadixital.es</TableCell>
            <TableCell><Badge className="bg-purple-100 text-purple-800">Superadmin</Badge></TableCell>
            <TableCell><Badge className="bg-green-100 text-green-800">Activo</Badge></TableCell>
            <TableCell>-</TableCell>
            <TableCell className="text-right text-gray-400 text-sm">No editable</TableCell>
          </TableRow>
          
          {users.map(user => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Badge className={user.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                  {user.role === 'admin' ? 'Administrador' : 'Gestor'}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge className={user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  {user.is_active ? 'Activo' : 'Inactivo'}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-gray-500">
                {user.last_login ? new Date(user.last_login).toLocaleDateString('es-ES') : 'Nunca'}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(user)}
                  >
                    {user.is_active ? 'Desactivar' : 'Activar'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600"
                    onClick={() => handleDeleteUser(user)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {users.length === 0 && (
        <p className="text-center py-4 text-gray-500">No hay usuarios adicionales. Solo el administrador principal tiene acceso.</p>
      )}
    </div>
  );
};

// Componente de gestión de solicitudes
const RequestManagement = ({ authToken }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.get(`${API}/course-requests`, { headers });
      setRequests(response.data);
    } catch (error) {
      console.error('Error obteniendo solicitudes:', error);
      toast({
        title: "Error",
        description: error.response?.status === 401 
          ? "Debe iniciar sesión para ver las solicitudes"
          : "No se pudieron cargar las solicitudes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendiente', variant: 'outline', color: 'text-orange-600 border-orange-300' },
      approved: { label: 'Aprobada', variant: 'outline', color: 'text-green-600 border-green-300' },
      rejected: { label: 'Rechazada', variant: 'outline', color: 'text-red-600 border-red-300' },
      requires_supervisor: { label: 'Requiere Supervisor', variant: 'outline', color: 'text-blue-600 border-blue-300' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge variant={config.variant} className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const getCourseTypeLabel = (courseType) => {
    const courseLabels = {
      'afd-basic': 'AFD Básico',
      'afd-advanced': 'AFD Avanzado',
      'afd-instructor': 'AFD Instructor',
      'afd-recertification': 'Recertificación AFD'
    };
    return courseLabels[courseType] || courseType;
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.put(
        `${API}/course-requests/${requestId}/status?status=${newStatus}`, 
        {}, 
        { headers }
      );
      
      if (response.data.success) {
        toast({
          title: "Estado actualizado",
          description: `La solicitud ha sido ${newStatus === 'approved' ? 'aprobada' : 'rechazada'} correctamente`,
        });
        
        // Recargar solicitudes
        fetchRequests();
      }
    } catch (error) {
      console.error('Error actualizando estado:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado de la solicitud",
        variant: "destructive",
      });
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta solicitud?')) return;
    
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.delete(
        `${API}/course-requests/${requestId}`, 
        { headers }
      );
      
      if (response.data.success) {
        toast({
          title: "Solicitud eliminada",
          description: "La solicitud ha sido eliminada correctamente",
        });
        
        // Recargar solicitudes
        fetchRequests();
        setSelectedRequest(null);
      }
    } catch (error) {
      console.error('Error eliminando solicitud:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la solicitud",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Clock className="mr-2 h-4 w-4 animate-spin" />
            Cargando solicitudes...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Gestión de Solicitudes
        </CardTitle>
        <CardDescription>
          Administre las solicitudes de información de cursos AFD
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Solicitante</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Curso</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Puntuación reCAPTCHA</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell className="font-medium">{request.name}</TableCell>
                  <TableCell>{request.email}</TableCell>
                  <TableCell>{getCourseTypeLabel(request.course_type)}</TableCell>
                  <TableCell>{getStatusBadge(request.status)}</TableCell>
                  <TableCell>{formatDate(request.created_at)}</TableCell>
                  <TableCell>
                    {request.recaptcha_score ? (
                      <Badge variant="outline" className="text-xs">
                        {(request.recaptcha_score * 100).toFixed(0)}%
                      </Badge>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {request.status === 'pending' && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            onClick={() => updateRequestStatus(request.id, 'approved')}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Aprobar
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => updateRequestStatus(request.id, 'rejected')}
                          >
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Rechazar
                          </Button>
                        </>
                      )}
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedRequest(request)}
                      >
                        Ver detalles
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => deleteRequest(request.id)}
                        title="Eliminar solicitud"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {/* Modal de detalles */}
          {selectedRequest && (
            <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Detalles de la Solicitud</DialogTitle>
                  <DialogDescription>
                    Información completa de la solicitud #{selectedRequest.id}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Solicitante:</Label>
                    <p className="font-medium">{selectedRequest.name}</p>
                  </div>
                  <div>
                    <Label>Email:</Label>
                    <p className="font-medium">{selectedRequest.email}</p>
                  </div>
                  <div>
                    <Label>Curso solicitado:</Label>
                    <p className="font-medium">{getCourseTypeLabel(selectedRequest.course_type)}</p>
                  </div>
                  <div>
                    <Label>Estado actual:</Label>
                    <p>{getStatusBadge(selectedRequest.status)}</p>
                  </div>
                  {selectedRequest.message && (
                    <div>
                      <Label>Mensaje:</Label>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{selectedRequest.message}</p>
                    </div>
                  )}
                  <div>
                    <Label>Fecha de solicitud:</Label>
                    <p className="text-sm">{formatDate(selectedRequest.created_at)}</p>
                  </div>
                  <div>
                    <Label>IP del cliente:</Label>
                    <p className="text-sm font-mono">{selectedRequest.client_ip || 'No disponible'}</p>
                  </div>
                  <div>
                    <Label>Puntuación de seguridad:</Label>
                    <p className="text-sm">
                      {selectedRequest.recaptcha_score ? 
                        `${(selectedRequest.recaptcha_score * 100).toFixed(2)}% (${selectedRequest.recaptcha_score >= 0.5 ? 'Confiable' : 'Sospechoso'})` : 
                        'No disponible'
                      }
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          {requests.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p>No se encontraron solicitudes</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Componente principal de la aplicación
function App() {
  const [activeTab, setActiveTab] = useState('public');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));
  const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem('currentUser') || 'null'));
  const { toast } = useToast();

  useEffect(() => {
    // Cargar estadísticas del dashboard
    fetchDashboardStats();
    
    // Cargar script de reCAPTCHA si está configurado
    if (RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY !== 'your-recaptcha-site-key-here') {
      loadRecaptchaScript();
    }
  }, []);

  const loadRecaptchaScript = () => {
    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      document.head.appendChild(script);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const response = await axios.get(`${API}/stats/dashboard`, { headers });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleRequestSuccess = () => {
    // Actualizar estadísticas después de nueva solicitud
    fetchDashboardStats();
  };

  const handleLogin = async (username, password) => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await axios.post(`${API}/auth/login`, formData);

      if (response.data.success) {
        const { token, user } = response.data;
        setAuthToken(token);
        setCurrentUser(user);
        setIsAuthenticated(true);
        
        // Guardar en localStorage
        localStorage.setItem('authToken', token);
        localStorage.setItem('currentUser', JSON.stringify(user));
        
        toast({
          title: "¡Bienvenido!",
          description: `Hola ${user.name}, has iniciado sesión correctamente.`,
        });
        
        setActiveTab('dashboard');
        return true;
      }
    } catch (error) {
      console.error('Error en login:', error);
      toast({
        title: "Error de autenticación",
        description: error.response?.data?.detail?.message || "Credenciales incorrectas",
        variant: "destructive",
      });
      return false;
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    setActiveTab('public');
    
    toast({
      title: "Sesión cerrada",
      description: "Has cerrado sesión correctamente.",
    });
  };

  // Verificar autenticación al cargar
  useEffect(() => {
    if (authToken && currentUser) {
      setIsAuthenticated(true);
      // Si está autenticado y está en pestaña pública o login, cambiar a dashboard
      if (activeTab === 'public' || activeTab === 'login') {
        setActiveTab('dashboard');
      }
    } else {
      // Si no está autenticado, asegurar que esté en pestaña pública
      if (activeTab !== 'public' && activeTab !== 'login') {
        setActiveTab('public');
      }
    }
  }, [authToken, currentUser, activeTab]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo_icon.png" 
                alt="Nova Dixital" 
                className="h-10 w-10 rounded-lg"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Nova Dixital</h1>
                <p className="text-sm text-gray-500">Sistema de Gestión de Cursos AFD</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="text-green-600 border-green-300">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Sistema Activo
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Mostrar pestañas solo si está autenticado, o solo "Solicitar Información" si no */}
          <TabsList className={`grid w-full mb-8 ${isAuthenticated ? 'grid-cols-6' : 'grid-cols-2'}`}>
            <TabsTrigger value="public" className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4" />
              Solicitar Información
            </TabsTrigger>
            
            {/* Pestaña de acceso solo visible si no está autenticado */}
            {!isAuthenticated && (
              <TabsTrigger value="login" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Acceso Administrador
              </TabsTrigger>
            )}
            
            {/* Pestañas de admin solo visibles si está autenticado */}
            {isAuthenticated && (
              <>
                <TabsTrigger value="dashboard" className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Dashboard
                </TabsTrigger>
                <TabsTrigger value="management" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Gestión
                </TabsTrigger>
                <TabsTrigger value="admin" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Admin
                </TabsTrigger>
                <TabsTrigger value="config" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Configuración
                </TabsTrigger>
                <TabsTrigger value="audit-logs" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Auditoría
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Formulario público de solicitud */}
          <TabsContent value="public" className="space-y-6">
            <CourseRequestForm onSuccess={handleRequestSuccess} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-blue-600" />
                    Cursos Disponibles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="p-3 border rounded-lg">
                      <h4 className="font-semibold">AFD Básico</h4>
                      <p className="text-sm text-gray-600">Formación inicial - 40 horas</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <h4 className="font-semibold">AFD Avanzado</h4>
                      <p className="text-sm text-gray-600">Técnicas avanzadas - 60 horas</p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <h4 className="font-semibold">AFD Instructor</h4>
                      <p className="text-sm text-gray-600">Certificación para formar - 80 horas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="h-5 w-5 text-green-600" />
                    Certificación ISO 9001
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>
                        Nuestros cursos están certificados bajo normativa ISO 9001 
                        para garantizar la calidad educativa.
                      </AlertDescription>
                    </Alert>
                    <ul className="text-sm space-y-1">
                      <li>• Registros completos de auditoría</li>
                      <li>• Supervisión de dos gestores</li>
                      <li>• Comunicaciones automáticas</li>
                      <li>• Seguimiento personalizado</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pestaña de Acceso Administrador (solo visible si NO está autenticado) */}
          <TabsContent value="login" className="space-y-6">
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Acceso Administrador</h2>
              <p className="text-gray-600 mb-8">Inicie sesión para acceder al panel de gestión</p>
              <div className="max-w-md mx-auto">
                <LoginForm onLogin={handleLogin} />
              </div>
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-500 mb-3">¿Eres estudiante y tienes credenciales?</p>
                <Button 
                  variant="outline"
                  onClick={() => navigateTo('campus')}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                >
                  <User className="mr-2 h-4 w-4" />
                  Acceder al Campus Virtual
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Dashboard de estadísticas - Solo visible si está autenticado */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Dashboard de Gestión</h2>
              <div className="flex items-center gap-4">
                <Badge variant="outline">
                  <Calendar className="h-3 w-3 mr-1" />
                  Actualizado: {dashboardStats?.last_updated ? formatDate(dashboardStats.last_updated) : 'Cargando...'}
                </Badge>
                <Button variant="outline" onClick={handleLogout} size="sm">
                  Cerrar Sesión
                </Button>
              </div>
            </div>

            <DashboardStats stats={dashboardStats} />

                {dashboardStats && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Solicitudes por Tipo de Curso</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(dashboardStats.requests_by_type || {}).map(([type, count]) => (
                            <div key={type} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium">{type.replace('-', ' ').toUpperCase()}</span>
                              <Badge>{count}</Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Actividad Mensual</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {(dashboardStats.monthly_requests || []).map((month) => (
                            <div key={month.month} className="flex justify-between items-center p-2 border-b">
                              <span>{month.month}</span>
                              <span className="font-semibold">{month.count} solicitudes</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
          </TabsContent>

          {/* Gestión de solicitudes - Solo visible si está autenticado */}
          <TabsContent value="management" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Gestión de Solicitudes</h2>
              <div className="flex items-center gap-4">
                <Button onClick={() => window.location.reload()} variant="outline">
                  <Calendar className="h-4 w-4 mr-2" />
                  Actualizar
                </Button>
                <Button variant="outline" onClick={handleLogout} size="sm">
                  Cerrar Sesión
                </Button>
              </div>
            </div>

            <RequestManagement authToken={authToken} />
          </TabsContent>

          {/* Panel de administración - Solo visible si está autenticado */}
          <TabsContent value="admin" className="space-y-6">
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Panel de Administración</h2>
              <div className="flex justify-between items-center mb-6">
                <div className="text-left">
                  <p className="text-sm text-gray-600">Bienvenido, {currentUser?.name}</p>
                  <p className="text-xs text-gray-500">{currentUser?.email}</p>
                </div>
                <Button variant="outline" onClick={handleLogout} size="sm">
                  Cerrar Sesión
                </Button>
              </div>
                  
                  <div className="space-y-8">
                    {/* Gestión de Cursos - Ancho completo */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <BookOpen className="h-5 w-5" />
                          Gestión de Cursos
                        </CardTitle>
                        <CardDescription>
                          Crear, editar y gestionar todos los cursos AFD
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <CourseManagement authToken={authToken} />
                      </CardContent>
                    </Card>

                    {/* Gestión de Estudiantes - Ancho completo */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="h-5 w-5" />
                          Gestión de Estudiantes
                        </CardTitle>
                        <CardDescription>
                          Ver y gestionar estudiantes por curso
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <StudentManagement authToken={authToken} />
                      </CardContent>
                    </Card>

                    {/* Gestión de Administradores - Solo visible para superadmin */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="h-5 w-5" />
                          Gestión de Usuarios del Sistema
                        </CardTitle>
                        <CardDescription>
                          Añadir, editar y eliminar administradores y gestores
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <AdminUserManagement authToken={authToken} currentUser={currentUser} />
                      </CardContent>
                    </Card>

                    {/* Fila de configuración y ISO */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>Configuración del Sistema</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span>reCAPTCHA v3:</span>
                              <Badge variant={RECAPTCHA_SITE_KEY !== 'your-recaptcha-site-key-here' ? 'default' : 'secondary'}>
                                {RECAPTCHA_SITE_KEY !== 'your-recaptcha-site-key-here' ? 'Configurado' : 'Pendiente'}
                              </Badge>
                            </div>
                            <div className="flex justify-between">
                              <span>Entorno:</span>
                              <Badge variant="outline">
                                {process.env.REACT_APP_ENVIRONMENT || 'Desarrollo'}
                              </Badge>
                            </div>
                            <div className="flex justify-between">
                              <span>Versión:</span>
                              <Badge variant="outline">
                                {process.env.REACT_APP_VERSION || '1.0.0'}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle>Cumplimiento ISO 9001</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <Alert>
                              <CheckCircle2 className="h-4 w-4" />
                              <AlertDescription>
                                Todos los registros de auditoría se mantienen por 7 años según normativa ISO 9001.
                              </AlertDescription>
                            </Alert>
                            <Button 
                              variant="outline" 
                              className="w-full"
                              onClick={() => setActiveTab('audit-logs')}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Ver Logs de Auditoría
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
            </div>
          </TabsContent>

          {/* Pestaña de Logs de Auditoría - Solo visible si está autenticado */}
          <TabsContent value="audit-logs" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Logs de Auditoría ISO 9001</h2>
              <div className="flex items-center gap-4">
                <Button onClick={() => window.location.reload()} variant="outline">
                  <Calendar className="h-4 w-4 mr-2" />
                  Actualizar
                </Button>
                <Button variant="outline" onClick={handleLogout} size="sm">
                  Cerrar Sesión
                </Button>
              </div>
            </div>

            <AuditLogsComponent authToken={authToken} />
          </TabsContent>

          {/* Pestaña de Configuración - Panel completo con pestañas internas */}
          <TabsContent value="config" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Configuración del Sistema</h2>
              <div className="flex items-center gap-4">
                <Button variant="outline" onClick={handleLogout} size="sm">
                  Cerrar Sesión
                </Button>
              </div>
            </div>

            <ConfigurationPanel authToken={authToken} />
          </TabsContent>
        </Tabs>
      </main>

      <Toaster />
    </div>
  );
}

// ==================== CAMPUS VIRTUAL COMPONENTS ====================

// Componente de login para estudiantes
const StudentLoginForm = ({ onLogin, onGoogleLogin, onNavigateToAdmin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('email', formData.email);
      formDataObj.append('password', formData.password);

      const response = await axios.post(`${API}/student-auth/login`, formDataObj);

      if (response.data.success) {
        await onLogin(response.data.token, response.data.student);
        toast({
          title: "¡Bienvenido al Campus!",
          description: `Hola ${response.data.student.name}, acceso exitoso.`,
        });
      }
    } catch (error) {
      console.error('Error en login de estudiante:', error);
      toast({
        title: "Error de acceso",
        description: error.response?.data?.detail?.message || "Credenciales incorrectas",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsGoogleLoading(true);
    try {
      const response = await axios.post(`${API}/student-auth/google`, {
        credential: credentialResponse.credential
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data.success) {
        await onGoogleLogin(response.data.token, response.data.student);
        toast({
          title: "¡Bienvenido al Campus!",
          description: `Hola ${response.data.student.name}, acceso con Google exitoso.`,
        });
      }
    } catch (error) {
      console.error('Error en login con Google:', error);
      const errorMessage = error.response?.data?.detail?.message || 
                          error.response?.data?.message || 
                          "No se pudo iniciar sesión con Google";
      toast({
        title: "Error de acceso con Google",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast({
      title: "Error de Google",
      description: "No se pudo completar el inicio de sesión con Google. Inténtalo de nuevo.",
      variant: "destructive",
    });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-3 sm:px-4 py-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center px-4 sm:px-6">
          <img 
            src="/logo_icon.png" 
            alt="Nova Dixital" 
            className="h-14 w-14 sm:h-16 sm:w-16 rounded-full mx-auto mb-4"
          />
          <CardTitle className="text-xl sm:text-2xl font-bold text-gray-900">Campus Virtual</CardTitle>
          <CardDescription className="text-sm">
            Accede a tu área de estudiante
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          {/* Google Login Button */}
          {GOOGLE_CLIENT_ID && (
            <div className="mb-6">
              <div className="flex justify-center">
                {isGoogleLoading ? (
                  <div className="flex items-center justify-center py-3 px-4 border rounded-md text-sm">
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                    <span>Verificando con Google...</span>
                  </div>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    useOneTap={false}
                    theme="outline"
                    size="large"
                    text="signin_with"
                    shape="rectangular"
                    locale="es"
                    width="300"
                  />
                )}
              </div>
              
              <div className="relative my-4 sm:my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">
                    O continúa con email
                  </span>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="tu.email@ejemplo.com"
                required
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-sm">Contraseña</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Tu contraseña"
                required
                className="mt-1"
              />
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting || !formData.email || !formData.password}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Accediendo...
                </>
              ) : (
                <>
                  <User className="mr-2 h-4 w-4" />
                  Acceder al Campus
                </>
              )}
            </Button>

            <div className="text-center pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={onNavigateToAdmin}
                className="text-xs sm:text-sm"
              >
                <ArrowLeft className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                Acceso Administración
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Componente de cambio de contraseña
const ChangePasswordForm = ({ studentToken, onSuccess }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas no coinciden",
        variant: "destructive",
      });
      return;
    }

    if (formData.newPassword.length < 8) {
      toast({
        title: "Error",
        description: "La contraseña debe tener al menos 8 caracteres",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('current_password', formData.currentPassword);
      formDataObj.append('new_password', formData.newPassword);

      const response = await axios.post(`${API}/student-auth/change-password`, formDataObj, {
        headers: {
          'Authorization': `Bearer ${studentToken}`
        }
      });

      if (response.data.success) {
        toast({
          title: "¡Contraseña actualizada!",
          description: "Tu contraseña ha sido cambiada correctamente.",
        });
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        onSuccess();
      }
    } catch (error) {
      console.error('Error cambiando contraseña:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail?.message || "No se pudo cambiar la contraseña",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
          <Lock className="h-4 w-4 sm:h-5 sm:w-5" />
          Cambiar Contraseña
        </CardTitle>
        <CardDescription className="text-sm">
          Actualiza tu contraseña de acceso al campus
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="currentPassword" className="text-sm">Contraseña actual</Label>
            <Input
              id="currentPassword"
              name="currentPassword"
              type="password"
              value={formData.currentPassword}
              onChange={handleInputChange}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="newPassword" className="text-sm">Nueva contraseña</Label>
            <Input
              id="newPassword"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleInputChange}
              placeholder="Mínimo 8 caracteres"
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-sm">Confirmar nueva contraseña</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              className="mt-1"
            />
          </div>

          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? (
              <>
                <Clock className="mr-2 h-4 w-4 animate-spin" />
                Actualizando...
              </>
            ) : (
              <>
                <Lock className="mr-2 h-4 w-4" />
                Cambiar Contraseña
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Componente de perfil del estudiante
const StudentProfile = ({ student, studentToken }) => {
  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
          <User className="h-4 w-4 sm:h-5 sm:w-5" />
          Mi Perfil
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <Label className="text-xs sm:text-sm text-gray-500">Nombre completo</Label>
            <p className="font-medium text-sm sm:text-base">{student.name}</p>
          </div>
          <div>
            <Label className="text-xs sm:text-sm text-gray-500">Email</Label>
            <p className="font-medium text-sm sm:text-base break-all">{student.email}</p>
          </div>
          <div>
            <Label className="text-xs sm:text-sm text-gray-500">Estado</Label>
            <div className="mt-1">
              <Badge variant={student.status === 'approved' ? 'default' : 'secondary'} className="text-xs">
                {student.status === 'approved' ? 'Aprobado' : student.status}
              </Badge>
            </div>
          </div>
          <div>
            <Label className="text-xs sm:text-sm text-gray-500">Fecha de matrícula</Label>
            <p className="text-xs sm:text-sm">{student.enrollment_date ? formatDate(student.enrollment_date) : 'No especificada'}</p>
          </div>
          {student.course_id && (
            <div>
              <Label className="text-xs sm:text-sm text-gray-500">Curso asignado</Label>
              <p className="font-medium text-sm sm:text-base">{student.course_id.toUpperCase()}</p>
            </div>
          )}
          {student.last_login && (
            <div>
              <Label className="text-xs sm:text-sm text-gray-500">Último acceso</Label>
              <p className="text-xs sm:text-sm">{formatDate(student.last_login)}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Componente de cursos del estudiante
const StudentCourses = ({ studentToken }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/campus/courses`, {
        headers: { Authorization: `Bearer ${studentToken}` }
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error obteniendo cursos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los cursos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Clock className="mr-2 h-4 w-4 animate-spin" />
            Cargando cursos...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
          <BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />
          Mis Cursos
        </CardTitle>
        <CardDescription className="text-sm">
          Cursos en los que estás matriculado
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        {courses.length === 0 ? (
          <div className="text-center py-6 sm:py-8 text-gray-500">
            <BookOpen className="mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-4 opacity-50" />
            <p className="text-sm sm:text-base">No tienes cursos asignados aún</p>
          </div>
        ) : (
          <div className="space-y-4">
            {courses.map((course) => (
              <div key={course.id} className="border rounded-lg p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold truncate">{course.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{course.description}</p>
                  </div>
                  <Badge variant="outline" className="self-start sm:ml-4 shrink-0">
                    {course.duration_hours}h
                  </Badge>
                </div>
                
                {course.modules && course.modules.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-xs sm:text-sm font-medium mb-2">Módulos del curso:</h4>
                    <ul className="text-xs sm:text-sm space-y-1">
                      {course.modules.map((module, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle2 className="h-3 w-3 mr-2 text-green-500 mt-0.5 shrink-0" />
                          <span className="break-words">{module}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mt-3 pt-3 border-t">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                    <div className="flex items-center">
                      <span className="font-medium">Estado:</span>
                      <Badge variant="default" className="ml-2 text-xs">
                        {course.student_status === 'approved' ? 'Matriculado' : course.student_status}
                      </Badge>
                    </div>
                    {course.enrollment_date && (
                      <div className="text-gray-600">
                        <span className="font-medium">Matriculado:</span>
                        <span className="ml-1">{formatDate(course.enrollment_date)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Componente de mensajes del estudiante
const StudentMessages = ({ studentToken }) => {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/campus/messages`, {
        headers: { Authorization: `Bearer ${studentToken}` }
      });
      setMessages(response.data.messages || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error obteniendo mensajes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los mensajes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/campus/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${studentToken}` }
      });
      // Actualizar lista de mensajes
      fetchMessages();
    } catch (error) {
      console.error('Error marcando mensaje como leído:', error);
    }
  };

  const getMessageIcon = (type, priority) => {
    if (priority === 'urgent') return <AlertCircle className="h-4 w-4 text-red-500" />;
    if (priority === 'high') return <AlertCircle className="h-4 w-4 text-orange-500" />;
    return <MessageSquare className="h-4 w-4 text-blue-500" />;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Clock className="mr-2 h-4 w-4 animate-spin" />
            Cargando mensajes...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
          <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5" />
          Mensajes
          {unreadCount > 0 && (
            <Badge variant="destructive" className="ml-2 text-xs">
              {unreadCount} sin leer
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-sm">
          Comunicaciones de tus instructores
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        {messages.length === 0 ? (
          <div className="text-center py-6 sm:py-8 text-gray-500">
            <MessageSquare className="mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-4 opacity-50" />
            <p className="text-sm sm:text-base">No tienes mensajes</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4 max-h-80 sm:max-h-96 overflow-y-auto">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`border rounded-lg p-3 sm:p-4 ${!message.is_read ? 'border-blue-200 bg-blue-50' : ''}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex items-start gap-2">
                    {getMessageIcon(message.message_type, message.priority)}
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-sm sm:text-base truncate">{message.subject}</h4>
                      <p className="text-xs sm:text-sm text-gray-600">De: {message.sender_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 justify-between sm:justify-end">
                    <span className="text-xs text-gray-500">
                      {formatDate(message.created_at)}
                    </span>
                    {!message.is_read && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markAsRead(message.id)}
                        className="text-xs h-7 px-2"
                      >
                        <Eye className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">Marcar leído</span>
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 sm:mt-3">
                  <p className="text-xs sm:text-sm whitespace-pre-line">{message.message}</p>
                </div>
                {!message.is_read && (
                  <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t">
                    <Badge variant="outline" className="text-xs">
                      Sin leer
                    </Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== FIN CAMPUS VIRTUAL COMPONENTS ====================

// ==================== NUEVOS COMPONENTES DE GESTIÓN AVANZADA ====================

// Componente de gestión de cursos
const CourseManagement = ({ authToken }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/management/courses?include_inactive=true`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error obteniendo cursos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los cursos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (courseData) => {
    try {
      const response = await axios.post(`${API}/management/courses`, courseData, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data) {
        toast({
          title: "¡Curso creado!",
          description: `El curso "${courseData.title}" se ha creado correctamente`,
        });
        setShowCreateForm(false);
        fetchCourses();
      }
    } catch (error) {
      console.error('Error creando curso:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo crear el curso",
        variant: "destructive",
      });
    }
  };

  const handleUpdateCourse = async (courseId, courseData) => {
    try {
      const response = await axios.put(`${API}/management/courses/${courseId}`, courseData, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data) {
        toast({
          title: "¡Curso actualizado!",
          description: "El curso se ha actualizado correctamente",
        });
        setEditingCourse(null);
        fetchCourses();
      }
    } catch (error) {
      console.error('Error actualizando curso:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo actualizar el curso",
        variant: "destructive",
      });
    }
  };

  const handleToggleCourse = async (courseId, isActive) => {
    try {
      if (!isActive) {
        await axios.delete(`${API}/management/courses/${courseId}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        toast({
          title: "Curso desactivado",
          description: "El curso se ha desactivado correctamente",
        });
      } else {
        await axios.put(`${API}/management/courses/${courseId}`, 
          { is_active: true },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        
        toast({
          title: "Curso activado",
          description: "El curso se ha activado correctamente",
        });
      }
      
      fetchCourses();
    } catch (error) {
      console.error('Error cambiando estado del curso:', error);
      toast({
        title: "Error",
        description: "No se pudo cambiar el estado del curso",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Clock className="mr-2 h-4 w-4 animate-spin" />
            Cargando cursos...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Gestión de Cursos</h3>
        <Button onClick={() => setShowCreateForm(true)}>
          <BookOpen className="mr-2 h-4 w-4" />
          Crear Nuevo Curso
        </Button>
      </div>

      {/* Formulario de creación */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Crear Nuevo Curso</CardTitle>
          </CardHeader>
          <CardContent>
            <CourseForm 
              onSubmit={handleCreateCourse}
              onCancel={() => setShowCreateForm(false)}
            />
          </CardContent>
        </Card>
      )}

      {/* Lista de cursos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map((course) => (
          <Card key={course.id} className={`${course.is_active ? '' : 'opacity-60'}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-base">{course.title}</CardTitle>
                  <p className="text-sm text-gray-500">Código: {course.course_code}</p>
                </div>
                <Badge variant={course.is_active ? "default" : "secondary"}>
                  {course.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p><strong>Duración:</strong> {course.duration_hours} horas</p>
                <p><strong>Nivel:</strong> {course.level}</p>
                <p><strong>Categoría:</strong> {course.category}</p>
                <p className="text-gray-600">{course.description}</p>
                {course.modules && course.modules.length > 0 && (
                  <p><strong>Módulos:</strong> {course.modules.length}</p>
                )}
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setEditingCourse(course)}
                >
                  <Settings className="h-3 w-3 mr-1" />
                  Editar
                </Button>
                <Button 
                  size="sm" 
                  variant={course.is_active ? "destructive" : "default"}
                  onClick={() => handleToggleCourse(course.id, !course.is_active)}
                >
                  {course.is_active ? "Desactivar" : "Activar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Modal de edición */}
      {editingCourse && (
        <Dialog open={true} onOpenChange={() => setEditingCourse(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Curso: {editingCourse.title}</DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Detalles del Curso</TabsTrigger>
                <TabsTrigger value="students">Estudiantes Matriculados</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details">
                <CourseForm 
                  course={editingCourse}
                  onSubmit={(data) => handleUpdateCourse(editingCourse.id, data)}
                  onCancel={() => setEditingCourse(null)}
                />
              </TabsContent>
              
              <TabsContent value="students">
                <CourseStudentsList 
                  courseId={editingCourse.id}
                  courseName={editingCourse.title}
                  authToken={authToken}
                  onStudentRemoved={() => {
                    // Recargar cursos para actualizar contadores
                    fetchCourses();
                  }}
                />
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Formulario para crear/editar cursos
const CourseForm = ({ course, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    course_code: course?.course_code || '',
    title: course?.title || '',
    description: course?.description || '',
    duration_hours: course?.duration_hours || 40,
    max_students: course?.max_students || '',
    category: course?.category || 'afd',
    level: course?.level || 'basic',
    certification_type: course?.certification_type || 'AFD',
    prerequisites: course?.prerequisites || [],
    learning_objectives: course?.learning_objectives || [],
    instructor_notes: course?.instructor_notes || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Preparar datos para envío
      const submitData = {
        ...formData,
        duration_hours: parseInt(formData.duration_hours),
        max_students: formData.max_students ? parseInt(formData.max_students) : null,
        prerequisites: formData.prerequisites.filter(p => p.trim()),
        learning_objectives: formData.learning_objectives.filter(o => o.trim())
      };
      
      await onSubmit(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="course_code">Código del Curso</Label>
          <Input
            id="course_code"
            name="course_code"
            value={formData.course_code}
            onChange={handleInputChange}
            placeholder="ej: afd-advanced"
            required
          />
        </div>
        
        <div>
          <Label htmlFor="title">Título del Curso</Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="ej: AFD Avanzado"
            required
          />
        </div>
        
        <div>
          <Label htmlFor="duration_hours">Duración (horas)</Label>
          <Input
            id="duration_hours"
            name="duration_hours"
            type="number"
            value={formData.duration_hours}
            onChange={handleInputChange}
            min="1"
            required
          />
        </div>
        
        <div>
          <Label htmlFor="max_students">Máximo Estudiantes</Label>
          <Input
            id="max_students"
            name="max_students"
            type="number"
            value={formData.max_students}
            onChange={handleInputChange}
            placeholder="Dejar vacío para ilimitado"
          />
        </div>
        
        <div>
          <Label htmlFor="category">Categoría</Label>
          <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="afd">AFD</SelectItem>
              <SelectItem value="advanced">Avanzado</SelectItem>
              <SelectItem value="instructor">Instructor</SelectItem>
              <SelectItem value="recertification">Recertificación</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="level">Nivel</Label>
          <Select value={formData.level} onValueChange={(value) => setFormData(prev => ({ ...prev, level: value }))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="basic">Básico</SelectItem>
              <SelectItem value="intermediate">Intermedio</SelectItem>
              <SelectItem value="advanced">Avanzado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="description">Descripción</Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Descripción completa del curso"
          rows={3}
          required
        />
      </div>

      <div>
        <Label>Objetivos de Aprendizaje</Label>
        {formData.learning_objectives.map((objective, index) => (
          <div key={index} className="flex items-center gap-2 mt-2">
            <Input
              value={objective}
              onChange={(e) => handleArrayChange('learning_objectives', index, e.target.value)}
              placeholder={`Objetivo ${index + 1}`}
            />
            <Button 
              type="button"
              variant="outline"
              size="sm"
              onClick={() => removeArrayItem('learning_objectives', index)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
        <Button 
          type="button"
          variant="outline"
          onClick={() => addArrayItem('learning_objectives')}
          className="mt-2"
        >
          Agregar Objetivo
        </Button>
      </div>

      <div>
        <Label htmlFor="instructor_notes">Notas para Instructores</Label>
        <Textarea
          id="instructor_notes"
          name="instructor_notes"
          value={formData.instructor_notes}
          onChange={handleInputChange}
          placeholder="Notas adicionales para los instructores"
          rows={3}
        />
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Clock className="mr-2 h-4 w-4 animate-spin" />
              {course ? 'Actualizando...' : 'Creando...'}
            </>
          ) : (
            course ? 'Actualizar Curso' : 'Crear Curso'
          )}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
};

// Componente para listar estudiantes de un curso específico
const CourseStudentsList = ({ courseId, courseName, authToken, onStudentRemoved }) => {
  const [students, setStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [studentToAdd, setStudentToAdd] = useState('');
  const { toast } = useToast();

  const fetchCourseStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/students`, {
        params: { course_id: courseId, limit: 100 },
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error cargando estudiantes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los estudiantes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Obtener estudiantes sin curso asignado
  const fetchAvailableStudents = async () => {
    try {
      const response = await axios.get(`${API}/management/students`, {
        params: { limit: 200 },
        headers: { Authorization: `Bearer ${authToken}` }
      });
      // Filtrar estudiantes sin curso o con status approved/pending
      const allStudents = response.data.students || response.data || [];
      const available = allStudents.filter(s => 
        !s.course_id && s.status !== 'deleted' && s.is_active !== false
      );
      setAvailableStudents(available);
    } catch (error) {
      console.error('Error cargando estudiantes disponibles:', error);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchCourseStudents();
      fetchAvailableStudents();
    }
  }, [courseId]);

  const handleAddStudentToCourse = async () => {
    if (!studentToAdd) return;

    try {
      await axios.put(`${API}/management/students/${studentToAdd}`, 
        { course_id: courseId, status: 'approved' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      toast({
        title: "Estudiante añadido",
        description: "El estudiante ha sido matriculado en el curso",
      });

      setShowAddStudent(false);
      setStudentToAdd('');
      fetchCourseStudents();
      fetchAvailableStudents();
      if (onStudentRemoved) onStudentRemoved();
    } catch (error) {
      console.error('Error añadiendo estudiante:', error);
      toast({
        title: "Error",
        description: "No se pudo añadir el estudiante al curso",
        variant: "destructive",
      });
    }
  };

  const handleRemoveFromCourse = async (student) => {
    if (!window.confirm(`¿Estás seguro de dar de baja a "${student.name}" del curso "${courseName}"?`)) return;

    try {
      await axios.put(`${API}/management/students/${student.id}`, 
        { course_id: null, status: 'suspended' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      toast({
        title: "Estudiante dado de baja",
        description: `${student.name} ha sido dado de baja del curso`,
      });

      // Recargar lista
      fetchCourseStudents();
      fetchAvailableStudents();
      if (onStudentRemoved) onStudentRemoved();
    } catch (error) {
      console.error('Error dando de baja:', error);
      toast({
        title: "Error",
        description: "No se pudo dar de baja al estudiante",
        variant: "destructive",
      });
    }
  };

  const handleDeleteStudent = async (student) => {
    if (!window.confirm(`¿Estás seguro de ELIMINAR a "${student.name}" del sistema? Esta acción no se puede deshacer.`)) return;

    try {
      await axios.delete(`${API}/management/students/${student.id}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      toast({
        title: "Estudiante eliminado",
        description: `${student.name} ha sido eliminado del sistema`,
      });

      // Recargar lista
      fetchCourseStudents();
      if (onStudentRemoved) onStudentRemoved();
    } catch (error) {
      console.error('Error eliminando estudiante:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el estudiante",
        variant: "destructive",
      });
    }
  };

  const handleViewProfile = (student) => {
    setSelectedStudent(student);
    setShowProfileModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Clock className="h-6 w-6 animate-spin mr-2" />
        <span>Cargando estudiantes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">
          Estudiantes Matriculados ({students.length})
        </h3>
        <Button 
          variant={showAddStudent ? "outline" : "default"}
          onClick={() => setShowAddStudent(!showAddStudent)}
        >
          {showAddStudent ? 'Cancelar' : '+ Añadir Estudiante'}
        </Button>
      </div>

      {/* Formulario para añadir estudiante */}
      {showAddStudent && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>Seleccionar estudiante sin curso</Label>
                <Select value={studentToAdd} onValueChange={setStudentToAdd}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccione un estudiante..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableStudents.length > 0 ? (
                      availableStudents.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} - {s.email}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>No hay estudiantes disponibles</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleAddStudentToCourse}
                disabled={!studentToAdd || studentToAdd === 'none'}
                className="bg-green-600 hover:bg-green-700"
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Matricular
              </Button>
            </div>
            {availableStudents.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">
                No hay estudiantes sin curso asignado. Los estudiantes deben registrarse primero a través del formulario de solicitud.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {students.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No hay estudiantes matriculados en este curso</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Fecha Matrícula</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {students.map(student => (
            <TableRow key={student.id}>
              <TableCell className="font-medium">{student.name}</TableCell>
              <TableCell>{student.email}</TableCell>
              <TableCell>
                <Badge className={
                  student.status === 'approved' ? 'bg-green-100 text-green-800' :
                  student.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }>
                  {student.status === 'approved' ? 'Activo' : 
                   student.status === 'pending' ? 'Pendiente' : student.status}
                </Badge>
              </TableCell>
              <TableCell>
                {student.enrollment_date ? new Date(student.enrollment_date).toLocaleDateString('es-ES') : '-'}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewProfile(student)}
                    title="Ver perfil completo"
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Perfil
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    onClick={() => handleRemoveFromCourse(student)}
                    title="Dar de baja del curso"
                  >
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Baja
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => handleDeleteStudent(student)}
                    title="Eliminar estudiante"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}

      {/* Modal de perfil de estudiante */}
      {showProfileModal && selectedStudent && (
        <StudentProfileModal
          student={selectedStudent}
          authToken={authToken}
          onClose={() => {
            setShowProfileModal(false);
            setSelectedStudent(null);
          }}
          onStudentUpdated={() => {
            fetchCourseStudents();
            fetchAvailableStudents();
            if (onStudentRemoved) onStudentRemoved();
          }}
        />
      )}
    </div>
  );
};

// ==================== FIN NUEVOS COMPONENTES ====================

// Componente de gestión de estudiantes por curso
const StudentManagement = ({ authToken }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    course_id: '',
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [courses, setCourses] = useState([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchCourses();
    fetchStudents();
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [filters, pagination.page]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/management/courses`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error obteniendo cursos:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });

      // Solo agregar filtros que no sean vacíos o "todos"
      if (filters.course_id && filters.course_id !== 'todos') {
        params.append('course_id', filters.course_id);
      }
      if (filters.status && filters.status !== 'todos') {
        params.append('status', filters.status);
      }
      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }

      const response = await axios.get(`${API}/management/students?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      setStudents(response.data.students);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (error) {
      console.error('Error obteniendo estudiantes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los estudiantes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSendMessage = (student) => {
    setSelectedStudent(student);
    setShowMessageModal(true);
  };

  const handleViewStudentProfile = (student) => {
    setSelectedStudent(student);
    setShowProfileModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "Pendiente", variant: "secondary" },
      approved: { label: "Aprobado", variant: "default" },
      active: { label: "Activo", variant: "default" },
      completed: { label: "Completado", variant: "outline" },
      suspended: { label: "Suspendido", variant: "destructive" }
    };
    
    const config = statusConfig[status] || { label: status, variant: "secondary" };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Clock className="mr-2 h-4 w-4 animate-spin" />
            Cargando estudiantes...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Gestión de Estudiantes</h3>
        <Badge variant="outline">
          {pagination.total} estudiante{pagination.total !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Filtros mejorados */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros de Búsqueda</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Búsqueda por nombre, email o DNI</Label>
              <Input
                placeholder="Escribe para buscar..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div>
              <Label>Filtrar por curso</Label>
              <Select value={filters.course_id} onValueChange={(value) => handleFilterChange('course_id', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar curso" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">📚 Todos los cursos</SelectItem>
                  {courses.map((course) => (
                    <SelectItem key={course.id} value={course.id}>
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Filtrar por estado</Label>
              <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">🔄 Todos los estados</SelectItem>
                  <SelectItem value="pending">⏳ Pendiente</SelectItem>
                  <SelectItem value="approved">✅ Aprobado</SelectItem>
                  <SelectItem value="active">🟢 Activo</SelectItem>
                  <SelectItem value="completed">🏆 Completado</SelectItem>
                  <SelectItem value="suspended">❌ Suspendido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Botón para limpiar filtros */}
          {(filters.search || filters.course_id !== '' || filters.status !== '') && (
            <div className="mt-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setFilters({ course_id: '', status: '', search: '' })}
              >
                Limpiar filtros
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabla de estudiantes mejorada */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lista de Estudiantes</CardTitle>
        </CardHeader>
        <CardContent>
          {students.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <p>No se encontraron estudiantes con los filtros aplicados</p>
            </div>
          ) : (
            <div className="space-y-4">
              {students.map((student) => (
                <Card key={student.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-center">
                      <div className="lg:col-span-2">
                        <h4 className="font-medium text-lg">{student.name}</h4>
                        <p className="text-sm text-gray-600">{student.email}</p>
                        {student.dni && <p className="text-xs text-gray-500">DNI: {student.dni}</p>}
                        {student.phone && <p className="text-xs text-gray-500">Tel: {student.phone}</p>}
                      </div>
                      <div>
                        <p className="text-sm font-medium">Curso asignado</p>
                        <p className="text-sm text-gray-600">{student.course_title || 'Sin asignar'}</p>
                        {student.enrollment_date && (
                          <p className="text-xs text-gray-500">
                            Matriculado: {formatDate(student.enrollment_date)}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">Estado</p>
                        {getStatusBadge(student.status)}
                        {student.last_login && (
                          <p className="text-xs text-gray-500 mt-1">
                            Último acceso: {formatDate(student.last_login)}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="w-full"
                          onClick={() => handleViewStudentProfile(student)}
                        >
                          <User className="h-3 w-3 mr-1" />
                          Ver Perfil
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="w-full"
                          onClick={() => handleSendMessage(student)}
                        >
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Enviar Mensaje
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Paginación */}
          {pagination.pages > 1 && (
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <p className="text-sm text-gray-600">
                Página {pagination.page} de {pagination.pages} ({pagination.total} estudiantes)
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  ← Anterior
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={pagination.page === pagination.pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Siguiente →
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de mensajes */}
      {showMessageModal && selectedStudent && (
        <MessageModal
          student={selectedStudent}
          authToken={authToken}
          onClose={() => {
            setShowMessageModal(false);
            setSelectedStudent(null);
          }}
          onSuccess={() => {
            toast({
              title: "Mensaje enviado",
              description: `Mensaje enviado correctamente a ${selectedStudent.name}`,
            });
            setShowMessageModal(false);
            setSelectedStudent(null);
          }}
        />
      )}

      {/* Modal de perfil de estudiante */}
      {showProfileModal && selectedStudent && (
        <StudentProfileModal
          student={selectedStudent}
          authToken={authToken}
          onClose={() => {
            setShowProfileModal(false);
            setSelectedStudent(null);
          }}
          onStudentUpdated={() => {
            fetchStudents();
          }}
        />
      )}
    </div>
  );
};

// ==================== COMPONENTES DE GESTIÓN AVANZADA CONTINUADOS ====================

// Modal para enviar mensajes a estudiantes
const MessageModal = ({ student, authToken, onClose, onSuccess }) => {
  const [messageData, setMessageData] = useState({
    subject: '',
    message: '',
    priority: 'normal',
    send_email: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const { toast } = useToast();

  // Cargar plantillas desde la API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/management/messages/templates`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        // Filtrar solo las activas
        const activeTemplates = (response.data.templates || []).filter(t => t.is_active !== false);
        setTemplates(activeTemplates);
      } catch (error) {
        console.error('Error cargando plantillas:', error);
        // Si falla, usar plantillas por defecto
        setTemplates([
          { id: '1', name: 'Bienvenida al curso', subject: 'Bienvenida al curso', body: `Hola {nombre},\n\n¡Bienvenido/a a tu curso AFD!` },
          { id: '2', name: 'Recordatorio de documentos', subject: 'Recordatorio de documentos', body: `Hola {nombre},\n\nTe recordamos que necesitamos tus documentos.` }
        ]);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [authToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!messageData.subject.trim() || !messageData.message.trim()) {
      toast({
        title: "Error",
        description: "El asunto y el mensaje son obligatorios",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('student_id', student.id);
      formData.append('subject', messageData.subject.trim());
      formData.append('message', messageData.message.trim());
      formData.append('priority', messageData.priority);
      formData.append('send_email', messageData.send_email);

      const response = await axios.post(`${API}/management/messages/send`, formData, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.data.success) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo enviar el mensaje",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMessageData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Función para reemplazar variables en el mensaje
  const replaceVariables = (text) => {
    return text.replace(/{nombre}/gi, student.name);
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setMessageData(prev => ({
        ...prev,
        subject: template.subject,
        message: replaceVariables(template.body),
        priority: template.priority || 'normal'
      }));
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Enviar Mensaje a {student.name}
          </DialogTitle>
          <DialogDescription>
            Email: {student.email} | Curso: {student.course_title || 'Sin asignar'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Mensajes rápidos desde plantillas */}
          <div>
            <Label>Plantillas predefinidas (opcional)</Label>
            {loadingTemplates ? (
              <div className="text-sm text-gray-500 py-2">Cargando plantillas...</div>
            ) : (
              <Select onValueChange={handleTemplateSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar plantilla" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Las plantillas se pueden editar en Configuración → Plantillas de Mensajes
            </p>
          </div>

          {/* Asunto */}
          <div>
            <Label htmlFor="subject">Asunto *</Label>
            <Input
              id="subject"
              name="subject"
              value={messageData.subject}
              onChange={handleInputChange}
              placeholder="Asunto del mensaje"
              required
            />
          </div>

          {/* Mensaje */}
          <div>
            <Label htmlFor="message">Mensaje *</Label>
            <Textarea
              id="message"
              name="message"
              value={messageData.message}
              onChange={handleInputChange}
              placeholder="Escribe tu mensaje aquí..."
              rows={8}
              required
            />
          </div>

          {/* Prioridad */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="priority">Prioridad</Label>
              <Select value={messageData.priority} onValueChange={(value) => setMessageData(prev => ({...prev, priority: value}))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">📝 Normal</SelectItem>
                  <SelectItem value="high">⚠️ Alta</SelectItem>
                  <SelectItem value="urgent">🚨 Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Enviar también por email */}
            <div className="flex items-center space-x-2 mt-6">
              <input
                type="checkbox"
                id="send_email"
                name="send_email"
                checked={messageData.send_email}
                onChange={handleInputChange}
                className="w-4 h-4"
              />
              <Label htmlFor="send_email">Enviar también por email</Label>
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Enviar Mensaje
                </>
              )}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Modal para ver y EDITAR perfil de estudiante
const StudentProfileModal = ({ student, authToken, onClose, onStudentUpdated }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [courses, setCourses] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    fetchStudentProfile();
    fetchCourses();
  }, [student.id]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCourses(response.data.courses || response.data || []);
    } catch (error) {
      console.error('Error cargando cursos:', error);
    }
  };

  const fetchStudentProfile = async () => {
    try {
      const response = await axios.get(`${API}/management/students/${student.id}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      const data = response.data;
      const profile = {
        ...data.student,
        course: data.course_info,
        documents: data.documents || [],
        history: data.history || [],
        statistics: {
          total_documents: data.document_stats?.total || 0,
          unread_messages: 0,
          login_count: 0
        }
      };
      setProfileData(profile);
      setEditFormData({
        name: profile.name || '',
        email: profile.email || '',
        dni: profile.dni || '',
        phone: profile.phone || '',
        status: profile.status || 'pending',
        course_id: profile.course_id || '',
        notes: profile.notes || ''
      });
    } catch (error) {
      console.error('Error obteniendo perfil:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el perfil del estudiante",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Guardar cambios del perfil
  const handleSaveProfile = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/management/students/${student.id}`, editFormData, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      toast({
        title: "Perfil actualizado",
        description: "Los cambios se han guardado correctamente",
      });
      
      setIsEditing(false);
      fetchStudentProfile();
      if (onStudentUpdated) onStudentUpdated();
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el perfil",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Función para descargar documento
  const handleDownloadDocument = async (documentId) => {
    try {
      const response = await axios.get(`${API}/management/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${authToken}` },
        responseType: 'blob'
      });
      
      // Obtener nombre del archivo del header si existe
      const contentDisposition = response.headers['content-disposition'];
      let filename = `documento_${documentId}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando documento:', error);
      toast({
        title: "Error",
        description: "No se pudo descargar el documento",
        variant: "destructive",
      });
    }
  };

  // Función para visualizar documento en nueva ventana
  const handleViewDocument = (documentId) => {
    // Abrir en nueva ventana con el token de autenticación
    const viewUrl = `${API}/management/documents/${documentId}/view`;
    
    // Crear un formulario temporal para enviar el token
    const form = document.createElement('form');
    form.method = 'GET';
    form.action = viewUrl;
    form.target = '_blank';
    
    // Abrir directamente con fetch y crear blob URL
    axios.get(viewUrl, {
      headers: { Authorization: `Bearer ${authToken}` },
      responseType: 'blob'
    }).then(response => {
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    }).catch(error => {
      console.error('Error visualizando documento:', error);
      toast({
        title: "Error",
        description: "No se pudo visualizar el documento",
        variant: "destructive",
      });
    });
  };

  // Función para aprobar/rechazar documento
  const handleReviewDocument = async (documentId, status) => {
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append('review_action', status);  // Backend espera 'review_action', no 'status'
      formData.append('comments', status === 'approved' ? 'Documento aprobado' : 'Documento rechazado');

      await axios.put(`${API}/management/documents/${documentId}/review`, formData, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      toast({
        title: status === 'approved' ? "Documento aprobado" : "Documento rechazado",
        description: `El documento ha sido ${status === 'approved' ? 'aprobado' : 'rechazado'} correctamente`,
      });
      
      fetchStudentProfile();
    } catch (error) {
      console.error('Error revisando documento:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado del documento",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Función para eliminar documento
  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API}/management/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      toast({
        title: "Documento eliminado",
        description: "El documento ha sido eliminado correctamente",
      });
      
      fetchStudentProfile();
    } catch (error) {
      console.error('Error eliminando documento:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el documento",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Función para dar de baja al estudiante
  const handleDeactivateStudent = async () => {
    if (!window.confirm('¿Estás seguro de dar de baja a este estudiante? Esto lo desmatriculará del curso.')) return;
    
    setActionLoading(true);
    try {
      await axios.put(`${API}/management/students/${student.id}`, 
        { status: 'suspended', course_id: null },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      toast({
        title: "Estudiante dado de baja",
        description: "El estudiante ha sido dado de baja del curso",
      });
      
      if (onStudentUpdated) onStudentUpdated();
      onClose();
    } catch (error) {
      console.error('Error dando de baja:', error);
      toast({
        title: "Error",
        description: "No se pudo dar de baja al estudiante",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Función para eliminar estudiante
  const handleDeleteStudent = async () => {
    if (!window.confirm('¿Estás seguro de ELIMINAR este estudiante? Esta acción no se puede deshacer.')) return;
    
    setActionLoading(true);
    try {
      await axios.delete(`${API}/management/students/${student.id}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      toast({
        title: "Estudiante eliminado",
        description: "El estudiante ha sido eliminado del sistema",
      });
      
      if (onStudentUpdated) onStudentUpdated();
      onClose();
    } catch (error) {
      console.error('Error eliminando estudiante:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el estudiante",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex justify-between items-center">
            <div>
              <DialogTitle>Perfil de Estudiante</DialogTitle>
              <DialogDescription>
                {isEditing ? 'Editando información del estudiante' : 'Información completa del estudiante'}
              </DialogDescription>
            </div>
            {!loading && profileData && (
              <Button
                variant={isEditing ? "default" : "outline"}
                onClick={() => setIsEditing(!isEditing)}
                className="mr-8"
              >
                {isEditing ? (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Ver
                  </>
                ) : (
                  <>
                    <Settings className="h-4 w-4 mr-2" />
                    Editar
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : profileData ? (
          <div className="space-y-6">
            {/* Información Personal - Modo Vista o Edición */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Información Personal
              </h3>
              
              {isEditing ? (
                <div className="grid grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
                  <div>
                    <Label htmlFor="edit_name">Nombre *</Label>
                    <Input
                      id="edit_name"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_email">Email *</Label>
                    <Input
                      id="edit_email"
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_dni">DNI</Label>
                    <Input
                      id="edit_dni"
                      value={editFormData.dni}
                      onChange={(e) => setEditFormData({...editFormData, dni: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_phone">Teléfono</Label>
                    <Input
                      id="edit_phone"
                      value={editFormData.phone}
                      onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_status">Estado</Label>
                    <Select 
                      value={editFormData.status} 
                      onValueChange={(value) => setEditFormData({...editFormData, status: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="approved">Aprobado/Activo</SelectItem>
                        <SelectItem value="suspended">Suspendido/Baja</SelectItem>
                        <SelectItem value="rejected">Rechazado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="edit_course">Curso Asignado</Label>
                    <Select 
                      value={editFormData.course_id || ''} 
                      onValueChange={(value) => setEditFormData({...editFormData, course_id: value === 'none' ? null : value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sin curso asignado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin curso asignado</SelectItem>
                        {courses.map(course => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="edit_notes">Notas internas</Label>
                    <Textarea
                      id="edit_notes"
                      value={editFormData.notes}
                      onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                      placeholder="Notas visibles solo para administradores..."
                      rows={2}
                    />
                  </div>
                  <div className="col-span-2 flex gap-2">
                    <Button onClick={handleSaveProfile} disabled={actionLoading}>
                      {actionLoading ? (
                        <>
                          <Clock className="mr-2 h-4 w-4 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Guardar Cambios
                        </>
                      )}
                    </Button>
                    <Button variant="outline" onClick={() => setIsEditing(false)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Nombre</label>
                    <p className="font-medium">{profileData.name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Email</label>
                    <p className="font-medium">{profileData.email}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">DNI</label>
                    <p className="font-medium">{profileData.dni || 'No especificado'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Teléfono</label>
                    <p className="font-medium">{profileData.phone || 'No especificado'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Estado</label>
                    <Badge className={
                      profileData.status === 'approved' ? 'bg-green-100 text-green-800' :
                      profileData.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {profileData.status === 'approved' ? 'Activo' : 
                       profileData.status === 'pending' ? 'Pendiente' : 
                       profileData.status === 'suspended' ? 'Baja' : profileData.status}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Fecha de matriculación</label>
                    <p className="font-medium">
                      {profileData.enrollment_date 
                        ? new Date(profileData.enrollment_date).toLocaleDateString('es-ES')
                        : 'No especificado'}
                    </p>
                  </div>
                  {profileData.notes && (
                    <div className="col-span-2">
                      <label className="text-sm text-gray-600">Notas internas</label>
                      <p className="font-medium text-gray-700 bg-gray-50 p-2 rounded">{profileData.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Gestión de Cursos */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center">
                <BookOpen className="h-5 w-5 mr-2" />
                Gestión de Cursos
              </h3>
              
              {profileData.course ? (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-900">{profileData.course.title}</p>
                      <p className="text-sm text-blue-700 mt-1">{profileData.course.description}</p>
                      <p className="text-xs text-blue-600 mt-2">
                        Matriculado desde: {profileData.enrollment_date ? new Date(profileData.enrollment_date).toLocaleDateString('es-ES') : 'No especificado'}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      onClick={async () => {
                        if (!window.confirm(`¿Dar de baja a ${profileData.name} del curso "${profileData.course.title}"?`)) return;
                        try {
                          await axios.put(`${API}/management/students/${student.id}`, 
                            { course_id: null },
                            { headers: { Authorization: `Bearer ${authToken}` } }
                          );
                          toast({ title: "Baja realizada", description: "El estudiante ha sido dado de baja del curso" });
                          fetchStudentProfile();
                          if (onStudentUpdated) onStudentUpdated();
                        } catch (error) {
                          toast({ title: "Error", description: "No se pudo dar de baja", variant: "destructive" });
                        }
                      }}
                    >
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Dar de baja
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-500 mb-3">El estudiante no está matriculado en ningún curso</p>
                </div>
              )}

              {/* Asignar a otro curso */}
              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm font-medium text-green-800 mb-2">
                  {profileData.course ? 'Cambiar a otro curso:' : 'Matricular en un curso:'}
                </p>
                <div className="flex gap-2">
                  <Select 
                    value={editFormData.course_id || 'none'} 
                    onValueChange={(value) => setEditFormData({...editFormData, course_id: value === 'none' ? null : value})}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Seleccionar curso..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin curso</SelectItem>
                      {courses.map(course => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={async () => {
                      if (!editFormData.course_id) {
                        toast({ title: "Error", description: "Selecciona un curso", variant: "destructive" });
                        return;
                      }
                      try {
                        await axios.put(`${API}/management/students/${student.id}`, 
                          { course_id: editFormData.course_id, status: 'approved' },
                          { headers: { Authorization: `Bearer ${authToken}` } }
                        );
                        toast({ title: "Curso asignado", description: "El estudiante ha sido matriculado en el curso" });
                        fetchStudentProfile();
                        if (onStudentUpdated) onStudentUpdated();
                      } catch (error) {
                        toast({ title: "Error", description: "No se pudo asignar el curso", variant: "destructive" });
                      }
                    }}
                    disabled={!editFormData.course_id || editFormData.course_id === profileData.course?.id}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    {profileData.course ? 'Cambiar' : 'Matricular'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Documentos */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Documentos ({profileData.documents?.length || 0})
              </h3>
              {profileData.documents && profileData.documents.length > 0 ? (
                <div className="space-y-2">
                  {profileData.documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                      <div className="flex-1">
                        <span className="text-sm font-medium">{doc.original_filename}</span>
                        <p className="text-xs text-gray-500">{doc.category} - {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('es-ES') : ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                          doc.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {doc.status === 'approved' ? 'Aprobado' : doc.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-blue-50 hover:bg-blue-100"
                          onClick={() => handleViewDocument(doc.id)}
                          title="Ver documento"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadDocument(doc.id)}
                          title="Descargar"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                        {doc.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() => handleReviewDocument(doc.id, 'approved')}
                              disabled={actionLoading}
                              title="Aprobar"
                            >
                              <CheckCircle2 className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => handleReviewDocument(doc.id, 'rejected')}
                              disabled={actionLoading}
                              title="Rechazar"
                            >
                              <AlertCircle className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleDeleteDocument(doc.id)}
                          disabled={actionLoading}
                          title="Eliminar"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">El estudiante no ha subido documentos</p>
              )}
            </div>

            {/* Estadísticas */}
            {profileData.statistics && (
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Estadísticas
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {profileData.statistics.total_documents || 0}
                    </p>
                    <p className="text-sm text-gray-600">Documentos</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {profileData.statistics.unread_messages || 0}
                    </p>
                    <p className="text-sm text-gray-600">Mensajes</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {profileData.statistics.login_count || 0}
                    </p>
                    <p className="text-sm text-gray-600">Accesos</p>
                  </div>
                </div>
              </div>
            )}

            {/* Historial de acciones - AL FINAL */}
            {profileData.history && profileData.history.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg mb-3 flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Historial Reciente
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto bg-gray-50 p-3 rounded">
                  {profileData.history.slice(0, 10).map(action => (
                    <div key={action.id} className="bg-white p-2 rounded text-sm border">
                      <div className="flex justify-between items-start">
                        <span className="font-medium text-xs">{action.action_type}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(action.timestamp).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                      <p className="text-gray-600 text-xs mt-1">{action.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p>No se pudo cargar el perfil</p>
        )}

        <div className="flex justify-between items-center border-t pt-4 mt-4">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              onClick={handleDeactivateStudent}
              disabled={actionLoading}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Dar de baja
            </Button>
            <Button 
              variant="outline" 
              className="text-red-600 border-red-300 hover:bg-red-50"
              onClick={handleDeleteStudent}
              disabled={actionLoading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </Button>
          </div>
          <Button onClick={onClose} variant="outline">Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Componente de gestión de documentos
// Utilidad para comprimir imágenes antes de subir (especialmente útil en móviles)
const compressImage = async (file, maxWidth = 1600, maxHeight = 1600, quality = 0.8) => {
  // Solo comprimir si es una imagen
  if (!file || !file.type || !file.type.startsWith('image/')) return file;

  // No comprimir si ya es pequeña (menos de 1MB)
  if (file.size < 1024 * 1024) return file;

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (!blob) {
            resolve(file); // Fallback al original si falla
            return;
          }
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          console.log(`Imagen comprimida: ${(file.size/1024/1024).toFixed(2)}MB -> ${(compressedFile.size/1024/1024).toFixed(2)}MB`);
          resolve(compressedFile);
        }, 'image/jpeg', quality);
      };
      img.onerror = () => resolve(file); // Fallback
    };
    reader.onerror = () => resolve(file); // Fallback
  });
};

const StudentDocuments = ({ studentToken }) => {
  const [documents, setDocuments] = useState([]);
  const [docsByCategory, setDocsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const categoryLabels = {
    'dni': 'DNI/Identificación',
    'matricula': 'Matrícula académica',
    'titulacion': 'Titulación previa',
    'demandante_empleo': 'Demandante de empleo',
    'fotografia': 'Fotografía tipo carnet',
    'otros': 'Otros documentos'
  };

  const statusLabels = {
    'pending': 'Pendiente revisión',
    'approved': 'Aprobado',
    'rejected': 'Rechazado',
    'requires_review': 'Requiere revisión'
  };

  const statusColors = {
    'pending': 'bg-yellow-100 text-yellow-800',
    'approved': 'bg-green-100 text-green-800',
    'rejected': 'bg-red-100 text-red-800',
    'requires_review': 'bg-orange-100 text-orange-800'
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/campus/documents`, {
        headers: { Authorization: `Bearer ${studentToken}` }
      });
      setDocuments(response.data.documents || []);
      setDocsByCategory(response.data.by_category || {});
    } catch (error) {
      console.error('Error obteniendo documentos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los documentos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (30MB max para permitir compresión posterior)
      if (file.size > 30 * 1024 * 1024) {
        toast({
          title: "Error",
          description: "El archivo es excesivamente grande. Máximo 30MB permitido.",
          variant: "destructive",
        });
        return;
      }

      // Si es una imagen grande, comprimirla inmediatamente
      if (file.type.startsWith('image/') && file.size > 1024 * 1024) {
        toast({
          title: "Procesando imagen",
          description: "Optimizando archivo para la subida...",
        });
        const compressed = await compressImage(file);
        setSelectedFile(compressed);
      } else {
        setSelectedFile(file);
      }
    }
  };

  const handleUpload = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    
    if (!selectedFile) {
      toast({
        title: "Error",
        description: "Por favor, selecciona un archivo primero",
        variant: "destructive",
      });
      return;
    }

    if (!selectedCategory) {
      toast({
        title: "Error",
        description: "Por favor, selecciona una categoría para el documento",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    toast({
      title: "Subiendo...",
      description: "Preparando archivo para enviar",
    });

    try {
      const formData = new FormData();
      // Usar el nombre del archivo explícitamente para asegurar compatibilidad
      formData.append('file', selectedFile, selectedFile.name);
      formData.append('category', selectedCategory);

      const response = await axios.post(`${API}/campus/documents/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${studentToken}`
        }
      });

      if (response.data.success) {
        toast({
          title: "¡Documento subido!",
          description: "El documento se ha subido correctamente",
        });
        setSelectedFile(null);
        setSelectedCategory('');
        // Reset file inputs
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (cameraInputRef.current) cameraInputRef.current.value = '';
        // Refresh documents
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error subiendo documento:', error);
      const errorMsg = error.response?.data?.detail || error.message || "Error desconocido";


      toast({
        title: "Error al subir",
        description: `${errorMsg} (Tipo: ${selectedFile?.type || 'desconocido'})`,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/campus/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${studentToken}` }
      });

      if (response.data.success) {
        toast({
          title: "Documento eliminado",
          description: "El documento se ha eliminado correctamente",
        });
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error eliminando documento:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo eliminar el documento",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/campus/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${studentToken}` },
        responseType: 'blob'
      });
      
      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Descarga iniciada",
        description: `Descargando ${doc.original_filename}`,
      });
    } catch (error) {
      console.error('Error descargando documento:', error);
      toast({
        title: "Error",
        description: "No se pudo descargar el documento",
        variant: "destructive",
      });
    }
  };

  const handleView = async (doc) => {
    try {
      // Abrir en nueva pestaña para visualizar
      const response = await axios.get(`${API}/campus/documents/${doc.id}/view`, {
        headers: { Authorization: `Bearer ${studentToken}` },
        responseType: 'blob'
      });
      
      // Crear URL del blob y abrir en nueva pestaña
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Limpiar después de un tiempo
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Error visualizando documento:', error);
      toast({
        title: "Error",
        description: "No se pudo visualizar el documento",
        variant: "destructive",
      });
    }
  };

  // Verificar si el documento es visualizable en el navegador
  const isViewable = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif'].includes(ext);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Clock className="mr-2 h-4 w-4 animate-spin" />
            Cargando documentos...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Formulario de subida */}
      <Card>
        <CardHeader className="px-4 sm:px-6">
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Upload className="h-4 w-4 sm:h-5 sm:w-5" />
            Subir Documento
          </CardTitle>
          <CardDescription className="text-sm">
            Sube la documentación requerida para tu matrícula
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="category" className="text-sm">Categoría del documento</Label>
              {/* Fallback nativo para móvil si el Select de Radix falla */}
              <div className="sm:hidden mt-1">
                <select
                  className="w-full flex h-10 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Selecciona una categoría</option>
                  {Object.entries(categoryLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="hidden sm:block">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-sm">Archivo</Label>
              
              {/* Input file oculto */}
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.heic,.heif,.webp"
                className="hidden"
              />
              
              {/* Input para captura de cámara en móvil */}
              <input
                ref={cameraInputRef}
                type="file"
                onChange={handleFileSelect}
                accept="image/*"
                capture="environment"
                className="hidden"
              />
              
              {/* Botones de selección */}
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-auto py-4 flex flex-col items-center gap-2"
                >
                  <FileText className="h-6 w-6 text-blue-600" />
                  <span className="text-sm">Seleccionar archivo</span>
                  <span className="text-xs text-gray-500">PDF, DOC, JPG, PNG...</span>
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => cameraInputRef.current?.click()}
                  className="w-full h-auto py-4 flex flex-col items-center gap-2 sm:hidden"
                >
                  <Upload className="h-6 w-6 text-green-600" />
                  <span className="text-sm">Usar cámara</span>
                  <span className="text-xs text-gray-500">Hacer foto del documento</span>
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 mt-2 text-center">
                Formatos: PDF, DOC, DOCX, JPG, PNG, GIF. Máx. 10MB.
              </p>
            </div>

            {selectedFile && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-900">Archivo seleccionado:</p>
                    <p className="text-sm text-blue-700 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-blue-600">
                      Tamaño: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      if (cameraInputRef.current) cameraInputRef.current.value = '';
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <Button 
              type="button"
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !selectedCategory}
              className="w-full mt-4"
            >
              {uploading ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Subir Documento
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de documentos */}
      <Card>
        <CardHeader className="px-4 sm:px-6">
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
            Mis Documentos
          </CardTitle>
          <CardDescription className="text-sm">
            Documentos subidos y su estado de revisión
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          {documents.length === 0 ? (
            <div className="text-center py-6 sm:py-8 text-gray-500">
              <FileText className="mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-4 opacity-50" />
              <p className="text-sm sm:text-base">No has subido documentos aún</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(categoryLabels).map(([category, label]) => {
                const categoryDocs = docsByCategory[category] || [];
                if (categoryDocs.length === 0) return null;

                return (
                  <div key={category}>
                    <h3 className="text-base sm:text-lg font-medium mb-2 sm:mb-3">{label}</h3>
                    <div className="space-y-2">
                      {categoryDocs.map((doc) => (
                        <div key={doc.id} className="border rounded-lg p-3 sm:p-4">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm sm:text-base truncate">{doc.original_filename}</h4>
                              <p className="text-xs sm:text-sm text-gray-600">
                                Subido: {formatDate(doc.uploaded_at)}
                              </p>
                              <p className="text-xs text-gray-500">
                                Tamaño: {(doc.file_size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 self-start">
                              <Badge className={`${statusColors[doc.status]} text-xs`}>
                                {statusLabels[doc.status]}
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Botones de acción */}
                          <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                            {/* Botón Visualizar - solo para archivos compatibles */}
                            {isViewable(doc.original_filename) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleView(doc)}
                                className="flex-1 sm:flex-none"
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                Ver
                              </Button>
                            )}
                            
                            {/* Botón Descargar - siempre disponible */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(doc)}
                              className="flex-1 sm:flex-none"
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Descargar
                            </Button>
                            
                            {/* Botón Eliminar - solo para pendientes o rechazados */}
                            {(doc.status === 'pending' || doc.status === 'rejected') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelete(doc.id)}
                                className="text-red-600 border-red-300 hover:bg-red-50 flex-1 sm:flex-none"
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Eliminar
                              </Button>
                            )}
                          </div>
                          
                          {doc.comments && (
                            <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-gray-50 rounded">
                              <p className="text-xs sm:text-sm font-medium">Comentarios:</p>
                              <p className="text-xs sm:text-sm">{doc.comments}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Componente principal del campus virtual
const CampusVirtual = ({ onNavigateToAdmin }) => {
  const [currentStudent, setCurrentStudent] = useState(null);
  const [studentToken, setStudentToken] = useState(localStorage.getItem('studentToken'));
  const [isStudentAuthenticated, setIsStudentAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState(localStorage.getItem('campusActiveTab') || 'profile');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Verificar autenticación del estudiante al cargar
    if (studentToken) {
      verifyStudentAuth();
    }
  }, [studentToken]);

  const verifyStudentAuth = async () => {
    try {
      const response = await axios.get(`${API}/campus/profile`, {
        headers: { Authorization: `Bearer ${studentToken}` }
      });
      setCurrentStudent(response.data);
      setIsStudentAuthenticated(true);
    } catch (error) {
      console.error('Error verificando autenticación:', error);
      handleStudentLogout();
    }
  };

  const handleStudentLogin = async (token, student) => {
    setStudentToken(token);
    setCurrentStudent(student);
    setIsStudentAuthenticated(true);
    localStorage.setItem('studentToken', token);
    localStorage.setItem('currentStudent', JSON.stringify(student));

    if (student.must_change_password) {
      setActiveTab('settings');
      localStorage.setItem('campusActiveTab', 'settings');
      toast({
        title: "Cambio de contraseña requerido",
        description: "Por seguridad, debes cambiar tu contraseña temporal.",
        variant: "destructive",
      });
    } else {
      setActiveTab('profile');
    }
  };

  // Login con Google - nunca requiere cambio de contraseña
  const handleGoogleStudentLogin = async (token, student) => {
    // Asegurar que must_change_password sea false para login con Google
    const googleStudent = { ...student, must_change_password: false };
    setStudentToken(token);
    setCurrentStudent(googleStudent);
    setIsStudentAuthenticated(true);
    localStorage.setItem('studentToken', token);
    localStorage.setItem('currentStudent', JSON.stringify(googleStudent));
    setActiveTab('profile');
    localStorage.setItem('campusActiveTab', 'profile');
  };

  const handleStudentLogout = () => {
    setStudentToken(null);
    setCurrentStudent(null);
    setIsStudentAuthenticated(false);
    localStorage.removeItem('studentToken');
    localStorage.removeItem('currentStudent');
    localStorage.removeItem('campusActiveTab');
    setActiveTab('profile');
  };

  const handlePasswordChanged = () => {
    // Actualizar el estado del estudiante
    if (currentStudent) {
      const updatedStudent = { ...currentStudent, must_change_password: false };
      setCurrentStudent(updatedStudent);
      localStorage.setItem('currentStudent', JSON.stringify(updatedStudent));
      setActiveTab('profile');
    }
  };

  // Si no está autenticado, mostrar formulario de login
  if (!isStudentAuthenticated) {
    return (
      <StudentLoginForm 
        onLogin={handleStudentLogin}
        onGoogleLogin={handleGoogleStudentLogin}
        onNavigateToAdmin={onNavigateToAdmin}
      />
    );
  }

  // Dashboard del campus virtual
  // Definición de las opciones del menú
  const menuItems = [
    { id: 'profile', label: 'Mi Perfil', icon: User },
    { id: 'courses', label: 'Mis Cursos', icon: BookOpen },
    { id: 'messages', label: 'Mensajes', icon: MessageSquare },
    { id: 'documents', label: 'Documentos', icon: FileText },
    { id: 'settings', label: 'Configuración', icon: Settings },
  ];

  const handleMenuItemClick = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem('campusActiveTab', tabId);
    setMobileMenuOpen(false);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    localStorage.setItem('campusActiveTab', value);
  };

  const currentMenuItem = menuItems.find(item => item.id === activeTab);
  const CurrentIcon = currentMenuItem?.icon || User;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header del campus - Responsive */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo y título - Responsive */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img 
                src="/logo_icon.png" 
                alt="Nova Dixital" 
                className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg"
              />
              <div>
                <h1 className="text-base sm:text-xl font-bold text-gray-900">Campus Virtual</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Nova Dixital AFD</p>
              </div>
            </div>
            
            {/* Usuario y logout - Responsive */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium truncate max-w-[150px]">{currentStudent?.name}</p>
                <p className="text-xs text-gray-500 truncate max-w-[150px]">{currentStudent?.email}</p>
              </div>
              <Button variant="outline" onClick={handleStudentLogout} size="sm" className="px-2 sm:px-3 h-8 sm:h-9">
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Salir</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Menú móvil hamburguesa - Solo visible en móvil */}
      <div className="sm:hidden bg-white border-b sticky top-14 z-40">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-full px-4 py-3 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <CurrentIcon className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-gray-900">{currentMenuItem?.label}</span>
          </div>
          <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {/* Dropdown del menú móvil */}
        {mobileMenuOpen && (
          <div className="absolute left-0 right-0 bg-white border-b shadow-lg">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleMenuItemClick(item.id)}
                  className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors
                    ${isActive 
                      ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600' 
                      : 'text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'
                    }`}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Overlay para cerrar menú */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 sm:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Contenido del campus */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Alerta de cambio de contraseña */}
        {currentStudent?.must_change_password && (
          <Alert className="mb-4 sm:mb-6 border-yellow-200 bg-yellow-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Acción requerida:</strong> Debes cambiar tu contraseña temporal. 
              <span className="hidden sm:inline"> Ve a "Configuración" para actualizar tu contraseña.</span>
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs para desktop - Solo mostramos la lista de pestañas */}
        <div className="hidden sm:block">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-5 mb-8">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <TabsTrigger key={item.id} value={item.id} className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </div>

        {/* Contenido unificado: Solo se renderiza UNA vez el componente activo */}
        <div className="space-y-6">
          {activeTab === 'profile' && (
            <StudentProfile student={currentStudent} studentToken={studentToken} />
          )}
          {activeTab === 'courses' && (
            <StudentCourses studentToken={studentToken} />
          )}
          {activeTab === 'messages' && (
            <StudentMessages studentToken={studentToken} />
          )}
          {activeTab === 'documents' && (
            <StudentDocuments studentToken={studentToken} />
          )}
          {activeTab === 'settings' && (
            <ChangePasswordForm 
              studentToken={studentToken} 
              onSuccess={handlePasswordChanged}
            />
          )}
        </div>
      </main>
    </div>
  );
};

// ==================== FIN CAMPUS VIRTUAL COMPONENTS ====================

// Componente para el chatbot de n8n - Implementación nativa React
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', content: '¡Hola! Soy tu asistente de Nova Dixital. ¿En qué puedo ayudarte con los cursos AFD?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Obtener o crear sessionId único para esta sesión de navegador
  const getSessionId = () => {
    let id = sessionStorage.getItem('n8n_chat_session');
    if (!id) {
      id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem('n8n_chat_session', id);
    }
    return id;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen, loading]);

  const handleSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setInput('');
    setLoading(true);

    try {
      // Formato solicitado por el usuario (Objeto JSON directo)
      const payload = {
        sessionId: getSessionId(),
        action: "sendMessage",
        chatInput: userMessage
      };

      // Usamos axios para enviar el mensaje al webhook de n8n
      const response = await axios.post(N8N_CHAT_URL, payload);

      // Formato de salida esperado: { "output": "..." }
      let botResponse = "Lo siento, no he podido procesar tu respuesta en este momento.";

      if (response.data && response.data.output) {
        botResponse = response.data.output;
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        botResponse = response.data[0].output || botResponse;
      } else if (typeof response.data === 'string') {
        botResponse = response.data;
      }

      setMessages(prev => [...prev, { role: 'bot', content: botResponse }]);
    } catch (error) {
      console.error('Error in chatbot connection:', error);
      setMessages(prev => [...prev, { role: 'bot', content: 'No he podido conectar con el servicio de asistencia. Por favor, inténtalo más tarde.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 p-4 bg-blue-600 text-white rounded-full shadow-xl hover:bg-blue-700 transition-all z-[1000] flex items-center justify-center transform hover:scale-110 active:scale-95"
        aria-label="Abrir chat de soporte"
      >
        <MessageSquare className="h-6 w-6" />
        <span className="absolute -top-1 -right-1 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-blue-500"></span>
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-[90vw] sm:w-[380px] h-[500px] max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col z-[1000] border border-gray-200 overflow-hidden animate-in slide-in-from-bottom-5">
      {/* Header del Chat */}
      <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white flex justify-between items-center shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm">Asistente Nova Dixital</h3>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
              <p className="text-[10px] text-blue-100">En línea ahora</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            title="Minimizar"
          >
            <Minus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Cuerpo de Mensajes */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
            {msg.role === 'bot' && (
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mr-2 border border-blue-200">
                <Bot className="h-4 w-4 text-blue-600" />
              </div>
            )}
            <div className={`max-w-[80%] p-3 rounded-2xl text-sm shadow-sm ${
              msg.role === 'user'
                ? 'bg-blue-600 text-white rounded-tr-none'
                : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none'
            }`}>
              {msg.content}
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0 ml-2">
                <User className="h-4 w-4 text-gray-600" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start items-center">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mr-2 border border-blue-200">
              <Bot className="h-4 w-4 text-blue-600" />
            </div>
            <div className="bg-white p-3 rounded-2xl rounded-tl-none border border-gray-100 shadow-sm">
              <div className="flex gap-1.5">
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"></div>
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input de Mensaje */}
      <div className="p-4 bg-white border-t border-gray-100">
        <form onSubmit={handleSendMessage} className="flex gap-2 items-center bg-gray-100 rounded-full px-4 py-1.5 focus-within:ring-2 focus-within:ring-blue-500/50 transition-all">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe tu consulta..."
            className="flex-1 bg-transparent border-none py-1.5 text-sm outline-none placeholder:text-gray-400"
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            className="p-1.5 text-blue-600 hover:text-blue-700 disabled:text-gray-400 transition-colors shrink-0"
          >
            <Send className="h-5 w-5" />
          </button>
        </form>
        <p className="text-[9px] text-center text-gray-400 mt-2">
          Asistente inteligente de Nova Dixital
        </p>
      </div>
    </div>
  );
};

// Componente principal que maneja el routing
const AppRouter = () => {
  const [currentRoute, setCurrentRoute] = useState(getCurrentRoute());

  useEffect(() => {
    const handlePopState = () => {
      setCurrentRoute(getCurrentRoute());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleNavigate = (route) => {
    navigateTo(route);
    setCurrentRoute(route);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Wrap with GoogleOAuthProvider only if GOOGLE_CLIENT_ID is configured
  const content = currentRoute === 'campus' 
    ? <CampusVirtual onNavigateToAdmin={() => handleNavigate('admin')} />
    : <App />;

  const contentWithChat = (
    <>
      {content}
      <Chatbot />
    </>
  );

  if (GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        {contentWithChat}
      </GoogleOAuthProvider>
    );
  }

  return contentWithChat;
};

export default AppRouter;